// src/features/data/avatars-tab.tsx
import {
  Chip,
  Kicker,
  LcpBreakdown,
  LifecycleChip,
  LifecycleReason,
  StatusDot,
  StatusPill,
} from "@/components/iris";
import type { SortDirection } from "@/components/iris";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { StandaloneTooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
// IR-1479 r3: the Platform cell renders the same brand glyph the Groups table
// uses. The app_type → glyph-code helper lives beside the icon (not in
// groups-unified-tab, which imports FROM this file).
import { PlatformIcon, platformToIconCode } from "@/features/links/PlatformIcon";
// IR-1351: the Notes cell reuses the Join Links search highlighter verbatim
// (IR-1233) rather than growing a second one — same <mark data-search-match>
// contract, same "re-centre a clamped value on the match" rule.
import { highlightMatches, snippetAroundMatch } from "@/features/links/highlight";
// IR-1351 r9: the Analytics donut, reused rather than re-implemented — same ring
// weight, same legend, same tooltip an operator already knows from Statistics.
import { DonutWithLegend } from "@/features/statistics/chart-primitives";
import {
  useAvatarClassificationOptions,
  useAvatarGroups,
  useAvatarReportedGroups,
  useCreateAvatar,
  useDeleteAvatar,
  useFleetAvatars,
  useResumeCollection,
  useUpdateAvatar,
} from "@/hooks/use-fleet";
import { fmtLocalDateTimeWithZone, viewerZoneLabel } from "@/lib/group-created";
import { breakdownEntries } from "@/lib/lifecycle-matrix";
import type { FleetAvatar, FleetAvatarGroup, FleetAvatarReportedGroup } from "@/lib/types";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Plus,
  Trash2,
} from "lucide-react";
import { type CSSProperties, Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CollectorsBlock, lastCollectedBy } from "./avatar-collectors";
import { CapacityEnvCaption, EnvPairCell, SHARED_LABEL, SharedLabel } from "./avatar-env-values";
import {
  ASSIGNED_ANALYST_OPTIONS,
  AVATARS_STATUS_COL_WIDTH,
  type AvatarKpiBucket,
  CAPACITY_SEATS_CAPTION,
  CAPACITY_TITLE,
  COLLECTION_FILTER_OPTIONS,
  DEFAULT_STATUS_FILTER,
  DELIVERING_COULD_NOT_CHECK_LABEL,
  DELIVERY_SPLIT_TITLE,
  ENV_INGESTION_FILTER_OPTIONS,
  type EnvIngestionPart,
  type EnvKey,
  GROUPS_HEADER_TITLE,
  LAST_MESSAGE_COLLECTOR_TITLE,
  LAST_MESSAGE_NONE_TITLE,
  LAST_MESSAGE_STORED_TITLE,
  REPORTING_FILTER_OPTIONS,
  ROSTER_READ_TITLE,
  archivedCellText,
  archivedCellTitle,
  archivedStillDelivering,
  avatarFilterCount,
  avatarHeadlineCounts,
  avatarKpiBucket,
  avatarLifecycleStatus,
  avatarResultLabel,
  canIssueResume,
  canIssueStop,
  canSubmitAvatarForm,
  capacitySeats,
  classificationFilterOptions,
  classificationsToPayload,
  collectedTitle,
  collectingSeats,
  collectionDetail,
  collectionLabel,
  collectionSortRank,
  collectionState,
  collectionVariant,
  connectedCollectionColor,
  connectedCollectionSlices,
  connectedEnvIngestionColor,
  connectedEnvIngestionSlices,
  coverFocusToInput,
  coverageGapIsWarning,
  coverageGapTitle,
  deliveringCouldNotCheckTitle,
  deliveringHeaderLabel,
  deliveringHeaderTitle,
  deliveringWindowDays,
  deliveryKindLabel,
  deliveryPillDetail,
  deliveryPillText,
  deliveryPillVariant,
  disconnectedRecencyRank,
  editStatusFields,
  envIngestionPairParts,
  envIngestionSortValue,
  envIngestionState,
  envPartTitle,
  fmtRelative,
  formatCapacity,
  formatCollected,
  formatDeliverySplit,
  formatGroupStatusSummary,
  formatLoad,
  formatSampleTime,
  groupDeliveryLabel,
  groupLastMessage,
  groupPhoneMark,
  groupRosterReadAt,
  groupsColumn,
  isAvatarFailing,
  isAvatarVisibleForAnalystFilter,
  isAvatarVisibleForClassificationFilter,
  isAvatarVisibleForCollectionFilter,
  isAvatarVisibleForEnvIngestionFilter,
  isAvatarVisibleForReportingFilter,
  isAvatarVisibleForStatusFilter,
  isFullyQuiet,
  isReadyToUseAvatar,
  isRetiredAvatar,
  isSignalPlatform,
  isStoppedAvatar,
  loadTitle,
  loadTone,
  matchesAvatarSearch,
  noGroupsRecheckDetail,
  offPhoneAnnotation,
  overCollectingTitle,
  parseCapacityLimit,
  parseCoverFocus,
  parseGeo,
  relinkActionLabel,
  reportStaleness,
  resumeErrorMessage,
  rosterRejection,
  rosterVerification,
  scraperGeneration,
  serializeGeo,
  settableStatusOptions,
  splitAvatarGroups,
  statusLabel,
  statusSortRank,
  stoppedDetail,
  summariseGroupStatuses,
  utcInstant,
} from "./avatars-tab.helpers";
import { CapacityDefaultsDialog } from "./capacity-defaults-dialog";
import {
  AVATAR_COLUMNS,
  type AvatarColumnKey,
  LOAD_COLUMN_LABEL,
  loadVisibleColumns,
  saveVisibleColumns,
} from "./column-visibility";
import { COUNTRIES } from "./countries";
import { FieldInfo } from "./field-info";
import {
  AVATARS_STATS_STORAGE_KEY,
  lockHeightFor,
  saveStatsCollapsed,
  shouldHideStatsForScroll,
  storedStatsPreference,
} from "./groups-stats-strip";
// IR-1697 r10: the drill-down rows reuse the Groups tab's PROD/DEV pair cell.
import { IngestionPairCell, ReceiptTimeText } from "./ingestion-pair-cell";
import { RelinkWizard } from "./relink-wizard";
import { useClientSort } from "./use-client-sort";

const RELINK_ENABLED = import.meta.env.VITE_RELINK_ENABLED === "true";

// ─── Helpers ───

// av.status is the live CANONICAL status (IR-1115 avatar_status.py), single-
// sourced from the heartbeat + DB and returned by /fleet/avatars. It already
// distinguishes active / stale / disconnected / new / blocked / cooldown /
// deleted, so we render it directly. (The old raw-`scraper_status` override
// masked canonical disconnected/new and is removed.) `scraper_status` stays
// available for IR-657 info.
//
// IR-1351 r11: the union gains ONE value the API never sends. "Ready to Use" is
// derived here from (connected AND no groups) rather than stored, because it is
// a statement about the avatar's assignments, not about its session — the
// backend would have to recompute it on every group attach/detach to keep a
// column honest that this expression answers for free.
export type DisplayStatus = FleetAvatar["status"] | "ready_to_use";

// The single seam. Everything downstream — the row pill, the Status filter, the
// KPI partition, the status sort, `isAvatarFailing` — reads the avatar's status
// through this function, so teaching it one derived value taught all of them.
export function effectiveStatus(av: FleetAvatar): DisplayStatus {
  // IR-1700 F1 (Rotem, comment 64954): a derived Disconnected beats a stored Ready
  // to Use. The backend sends a proven failure as `status: "disconnected"` while
  // the raw lifecycle value stays `db_status: "ready_to_use"`; reading the stored
  // value first showed that row as READY TO USE with no reason.
  // IR-1700 fix round F2 (Rotem, IR-1777 comment 65085): a derived Stale outranks it
  // too - "when the connection evidence is no longer valid and there is no explicit
  // proof of disconnection, show Stale; a saved Ready to Use cannot hide this" (dev
  // Mahdi2_WA2, id 120: status "stale" + db_status "ready_to_use" showed READY TO USE).
  // Only these two DERIVED words outrank the stored mark; a connected parked avatar
  // still reads Ready to Use (her last line), and every other state (stopped, blocked,
  // obsolete, cooldown, new, deleted) arrives as the canonical status itself and is
  // returned unchanged.
  if (av.status === "disconnected" || av.status === "stale") return av.status;
  if (isReadyToUseAvatar(av)) return "ready_to_use";
  return av.status;
}

// IR-1700 O1: how honest the start of a proven auth failure is. `report` and
// `collector` are the failure's own time; `last_success` is the last GOOD cycle
// (the failure began after it); `first_seen` is only when the backend first
// noticed. Anything unrecognised reads as the weakest claim, never the strongest.
export function authFailureSincePrefix(source: string | null | undefined): string {
  switch (source) {
    case "report":
    case "collector":
      return "since";
    case "last_success":
      return "since the last good cycle";
    default:
      return "at least since";
  }
}

// IR-1700 fix r1 (F1): the IR-1610 "since" line's tooltip. ONE claim per row: only a
// real report (or an IR-1610 recorded flip) is "recorded"; a bound says it is a bound.
// Unknown/missing sources keep the pre-IR-1700 wording.
export function disconnectedSinceTitle(source: string | null | undefined): string {
  switch (source) {
    case "evidence":
      return "last proof of connection";
    case "collector":
      return "reported by the collector";
    case "last_success":
      return "the last good cycle - the failure began after it";
    case "first_seen":
      return "when the system first noticed - the failure may have started earlier";
    default:
      return "recorded";
  }
}

// The IR-1610 line's prefix: an auth-stamp source reads exactly like the reason line
// beside it (one shared map); the IR-1610 sources (recorded / evidence) stay "since".
const AUTH_SINCE_SOURCES = new Set(["report", "collector", "last_success", "first_seen"]);
export function disconnectedSincePrefix(source: string | null | undefined): string {
  return source && AUTH_SINCE_SOURCES.has(source) ? authFailureSincePrefix(source) : "since";
}

// Handles both vocabularies so the pill survives the backend's
// avatar-connected-status rename (active → connected). Both values are in
// `FleetAvatar["status"]` since IR-1327 r3; the hand-written `| "connected"`
// widening this signature used to carry is no longer needed.
export function statusVariant(status: DisplayStatus) {
  switch (status) {
    case "active":
      return "ok" as const;
    case "disconnected":
      return "disconnected" as const;
    case "stopped":
      return "stopped" as const;
    case "blocked":
      return "banned" as const;
    case "cooldown":
      return "warn" as const;
    case "connected":
      return "ok" as const;
    case "stale":
      return "warn" as const;
    case "new":
      return "idle" as const;
    case "deleted":
      return "idle" as const;
    // IR-1351 r6: retired hardware is not an alarm — a muted pill, like `new`
    // and `deleted`, rather than a red one that would read as a failure.
    case "obsolete":
      return "idle" as const;
    // IR-1351 r11: muted, not green. A ready-to-use avatar is healthy, but green
    // is this kit's "measurably working" signal (IR-1327 r6 took it away from
    // every unmeasured claim) and nothing is being collected here. Muted also
    // keeps it from competing with the amber and red pills an operator scans for.
    case "ready_to_use":
      return "idle" as const;
    default:
      return "idle" as const;
  }
}

// The backend's human one-liner for the derived status (status_reason from
// avatar_status.status_reason), shown as the pill's hover tooltip.
export function statusTitle(av: FleetAvatar): string | undefined {
  return av.status_reason ?? undefined;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// IR-1707: date AND hour. A collector reports every few hours, and the reporter
// asked for "the real last message/report time" — a bare date cannot tell this
// morning's read from this evening's. h23 so midnight is never "24:00".
// IR-1425 r12b (Rotem 2026-10-12, comment 64191): the zone is printed after the
// time so the reader knows whose clock "08:38" is on. The fields feeding each
// expander line are unchanged (IR-1707 sources); only the zone becomes visible.
function fmtDateTime(iso: string | null): string {
  if (!iso) return "\u2014";
  const local = new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
  return `${local} ${viewerZoneLabel(iso)}`;
}

// ─── KPI Row ───

// IR-1705: every number the statistics strip shows, computed in ONE place so
// the full strip (`KpiRow`) and the one-line bar that replaces it once the
// operator scrolls into the list (`KpiBar`) cannot disagree. Two copies of the
// bucketing below is how a bar would one day read "Disconnected 4" over a
// strip that said 5. `null` / `[]` when the load failed (`avatars` undefined),
// which both renderers turn into "—" rather than a confident 0 (IR-1412).
export function avatarKpiSummary(avatars: FleetAvatar[] | undefined): {
  buckets: Record<AvatarKpiBucket, number> | null;
  total: number | null;
  connectedTotal: number | null;
  collectionMix: ReturnType<typeof connectedCollectionSlices>;
  // IR-1697 r11: the ingestion ring exists once per environment, never as one
  // environment-less list (Rotem 64438: "separate for PROD and for DEV").
  ingestionMixProd: ReturnType<typeof connectedEnvIngestionSlices>;
  ingestionMixDev: ReturnType<typeof connectedEnvIngestionSlices>;
} {
  const buckets =
    avatars?.reduce<Record<AvatarKpiBucket, number>>(
      (acc, a) => {
        // IR-1727: lifecycle wins before the derived status is even consulted —
        // a resurrected-deleted avatar (`status: 'connected', db_status:
        // 'deleted'`) must not inflate Connected the moment it is fetched (the
        // Deleted chip turns `include_deleted` on). It gets no tile at all; the
        // Obsolete branch below is the one retired status that keeps its own.
        if (isRetiredAvatar(a)) {
          if (avatarLifecycleStatus(a) === "obsolete") acc.obsolete += 1;
          return acc;
        }
        acc[avatarKpiBucket(effectiveStatus(a))] += 1;
        return acc;
      },
      { connected: 0, ready: 0, stale: 0, disconnected: 0, stopped: 0, other: 0, obsolete: 0 },
    ) ?? null;
  return {
    buckets,
    // IR-1351 r6: Total counts the FLEET — Obsolete is outside it (see KpiRow).
    total:
      buckets != null
        ? buckets.connected +
          buckets.ready +
          buckets.stale +
          buckets.disconnected +
          buckets.stopped +
          buckets.other
        : null,
    connectedTotal: buckets?.connected ?? null,
    // IR-1351 r10/r11 — the two rings, over the same charted-connected avatars.
    collectionMix: connectedCollectionSlices(avatars),
    ingestionMixProd: connectedEnvIngestionSlices(avatars, "prod"),
    ingestionMixDev: connectedEnvIngestionSlices(avatars, "dev"),
  };
}

// IR-1327 r2: every tile counts the SAME array the table renders, so the header
// can never contradict the footer or the Monitoring panel again.
//
// Before: "Total Avatars" came from `/fleet/stats` (a server count that excludes
// soft-deleted rows) while Active/Stale were counted client-side over the raw
// list (which included them) — on prod that read "Total 57 / Active 31" above a
// footer saying "63 of 68 avatars", next to Monitoring's 57. Three populations,
// one screen. The list endpoint now excludes deleted rows by default, and
// counting all three tiles off that one array is what keeps them aligned.
//
// The `stats.active_avatars` loading fallback is gone with it: it counted the
// raw DB column `status='active'` (41 on prod, pre-rename vocabulary), so it
// flashed a number that matched nothing before settling.
//
// IR-1327 r3 (Rotem: "I see 57 and not 58 - please add 'disconnected' status to
// the metric"): the strip read Total 57 / Connected 33 / Stale 5, so 19 avatars
// belonged to no tile at all and the numbers read as though they failed to add
// up. The status tiles are now a TOTAL PARTITION of the same array (see
// `avatarKpiBucket`), so Connected + Ready to Use + Stale + Disconnected + Other
// always equals Total - the strip proves its own arithmetic instead of leaving
// the reader to check it against the footer. ("Connected" is the ring centre
// since r10; "Ready to Use" is the sixth term r11 added.)
function KpiRow({
  avatars,
  disconnectedActive,
  onToggleDisconnected,
}: {
  avatars?: FleetAvatar[] | undefined;
  /** IR-1610 r2: the Disconnected tile doubles as a filter toggle. */
  disconnectedActive: boolean;
  onToggleDisconnected: () => void;
}) {
  // IR-1705: counted by `avatarKpiSummary`, shared with the one-line `KpiBar`.
  const { buckets, total, connectedTotal, collectionMix, ingestionMixProd, ingestionMixDev } =
    avatarKpiSummary(avatars);
  // IR-1351 r6: Total counts the FLEET, so obsolete avatars come out of it —
  // Rotem's words were "not part of the fleet". They are still listed below
  // (that is the mark she asked for), and their count gets its own tile, so the
  // excluded rows are accounted for rather than silently missing.
  //
  // IR-1351 r10 (Rotem): "you can remove the connected box". Connected is no
  // longer a tile — it is the CENTRE TOTAL of both donuts below, which is what
  // preserves the IR-1327 r3 invariant she is used to reading: Connected (the
  // ring) + Stale + Disconnected + Other still equals Total exactly. Dropping
  // the number entirely would have left four tiles that visibly fail to add up.
  const items = [
    {
      label: "Total Avatars",
      value: total,
      // IR-1727: 'Deleted avatars are excluded unless the Deleted status
      // filter is selected' was the stale half of this sentence — it is what
      // let a resurrected-deleted row inflate Total the moment the Deleted
      // chip widened the fetch. Both retired statuses are ALWAYS excluded
      // here, independent of any filter, like `avatarHeadlineTotal` below.
      hint: "The fleet: every avatar listed below except the Obsolete and Deleted ones — always, even when a status filter widens what the table shows. Connected is not a tile — it is the total in the middle of the two rings, so Ready to Use, Stale, Disconnected, Stopped and Other plus that ring add up to this one.",
    },
    {
      // IR-1351 r11 (Rotem): "Create another status for Avatars which are
      // connected with '0' groups - 'Ready to Use' - add this status in the box".
      label: "Ready to Use",
      value: buckets?.ready ?? null,
      hint: "Linked and healthy, but no groups assigned yet — spare capacity waiting for work. Counted here rather than inside the rings, so an avatar with nothing to collect no longer sits in the 'Running, no data' slice as though it had gone quiet.",
    },
    { label: "Stale", value: buckets?.stale ?? null, hint: "No new data in the last 12h." },
    {
      label: "Disconnected",
      value: buckets?.disconnected ?? null,
      // IR-1610 r2 (Rotem): "clearly identify which avatars disconnected" —
      // this tile is the one-click way to the list of them. (The DATE half of
      // that ask is the "since …" line under the Status pill.)
      // IR-1769 (O-21): an operator Stop is its own status now (next tile), so
      // this hint no longer says "or stopped by an operator".
      hint: "Logged out: the session is gone. Click to list only the disconnected avatars; click again to restore the default view.",
    },
    {
      // IR-1769 (O-21, Rotem 64931): Stopped is its own Avatar Status.
      label: "Stopped",
      value: buckets?.stopped ?? null,
      hint: "Stopped by an operator (Stop collection). It stays Stopped until someone presses Resume collection; a heartbeat or incoming data does not change it. A stopped avatar does not count as collecting.",
    },
    {
      label: "Other",
      value: buckets?.other ?? null,
      // IR-1727: Deleted no longer joins this tile under any filter — it has
      // no tile of its own (same as before), but it also no longer counts
      // toward Other or toward Total; see `isRetiredAvatar` above.
      hint: "New and Suspended (blocked / cooldown). Deleted avatars are never counted here or in Total — open the Deleted status filter to list them in the table below.",
    },
    {
      label: "Obsolete",
      value: buckets?.obsolete ?? null,
      hint: "Avatars whose handset we no longer have. Listed and marked below, but not counted as part of the fleet — so they are outside Total.",
    },
  ];
  // IR-1351 r10 — the two questions the tiles cannot answer, both taken over the
  // SAME population so the two rings are directly comparable and their centres
  // agree with each other. r11 narrows that population to Connected MINUS
  // ready-to-use (see `isChartedConnectedAvatar`) and charts every status the
  // matching filter chip offers, zeros included. (Computed in
  // `avatarKpiSummary` since IR-1705.)
  return (
    // IR-1351 r11 (Rotem): "the two 'Connected' graphs - display in the same row
    // as other boxes - like it was previously - this takes too much space and no
    // room for the table". r10 stacked a tile row ABOVE a chart row, which cost
    // the table roughly 280px of the screen. This is r9's shape again: ONE row,
    // the tiles reflowing into a compact block on the left and the two rings
    // taking fixed columns on the right, so the strip is as tall as a ring
    // (~170px) instead of a ring plus a tile row.
    <div
      style={{
        // Flex-wrap rather than three fixed grid columns: the three blocks need
        // ~870px side by side, and a grid would overflow the card horizontally
        // below that instead of stacking. Wrapping degrades to two rows and then
        // three, which is the r10 layout again only at the widths where it is
        // genuinely the only thing that fits.
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "stretch",
      }}
    >
      {/* Six tiles as a fixed 3x2 block, not `auto-fit`. Auto-fit packs as many
          120px columns as the space allows, which on a wide screen is four — so
          the six tiles landed 4 + 2 and left a visible gap beside Obsolete. This
          strip has drawn a layout complaint from Rotem in three consecutive
          rounds; a ragged grid is the kind of thing that buys a fourth. Three
          explicit columns wrap identically at every width, and `minmax(0, 1fr)`
          lets them shrink below their content rather than overflow the card when
          the rings claim their minimum. The tiles carry two-digit counts, so
          there is room to give. */}
      <div
        style={{
          flex: "1 1 260px",
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gridAutoRows: "min-content",
          gap: 8,
          alignContent: "start",
        }}
      >
        {items.map(({ label, value, hint }) => {
          // Tile labels ("Disconnected", "Obsolete") also occur as row pills and
          // filter chips, so a text lookup is ambiguous — tests resolve tiles by
          // this id instead.
          const testId = `avatar-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`;
          const body = (
            <>
              <Kicker variant="muted">{label}</Kicker>
              <div className="iris-kpi" style={{ marginTop: 2, fontSize: 18 }}>
                {value != null ? value.toLocaleString() : "\u2014"}
              </div>
            </>
          );
          // IR-1610 r2: the Disconnected tile is a real <button> — same testid,
          // class and markup as its siblings so tests and styling resolve it the
          // same way — with `aria-pressed` reflecting the filter and an inset
          // ring when active. Colour is not the only signal: the pressed state
          // is exposed to assistive tech, and the Status chip badge reads "1".
          if (label === "Disconnected") {
            return (
              <button
                key={label}
                type="button"
                data-testid={testId}
                className="iris-card"
                title={hint}
                aria-pressed={disconnectedActive}
                onClick={onToggleDisconnected}
                style={{
                  padding: "8px 10px",
                  cursor: "pointer",
                  textAlign: "left",
                  font: "inherit",
                  color: "inherit",
                  boxShadow: disconnectedActive
                    ? "inset 0 0 0 1px var(--iris-neon-cyan)"
                    : undefined,
                }}
              >
                {body}
              </button>
            );
          }
          return (
            <div
              key={label}
              data-testid={testId}
              className="iris-card"
              style={{ padding: "8px 10px" }}
              title={hint}
            >
              {body}
            </div>
          );
        })}
      </div>
      <ConnectedDonut
        testId="avatar-kpi-connected-collection"
        title="Connected, by collection"
        hint="Connected avatars that have groups assigned, split by what the Collection column says about each one. The slice to watch is 'Running, no data': the session is up but nothing has landed in 24h. Every Collection status is listed; one reading 0 cannot occur on a connected avatar — 'Not initiated' is a never-linked one and 'Stopped' one an operator stopped (the Stopped tile), counted in the Other, Disconnected, Stopped and Obsolete tiles instead. Avatars with no groups are in the Ready to Use tile, not here."
        data={collectionMix}
        colorFor={connectedCollectionColor}
        total={connectedTotal ?? 0}
        empty="No connected avatars to chart."
      />
      {/* IR-1697 r10/r11: environment-specific statistics, one ring per environment
          (the ticket: "explicitly environment-specific"; Rotem 64438: "The summary
          will be separate for PROD and for DEV"). No environment-less ring. */}
      <ConnectedDonut
        testId="avatar-kpi-connected-ingestion-prod"
        title="Connected, by ingestion (Production)"
        hint="The same avatars, split by what PROD alone says about each one. Could Not Check means this deployment cannot read PROD; Not Applicable means the avatar has no groups. Neither is counted as No data."
        data={ingestionMixProd}
        colorFor={connectedEnvIngestionColor}
        total={connectedTotal ?? 0}
        empty="No connected avatars to chart."
      />
      <ConnectedDonut
        testId="avatar-kpi-connected-ingestion-dev"
        title="Connected, by ingestion (Development)"
        hint="The same avatars, split by what DEV alone says about each one. Could Not Check means this deployment cannot read DEV; Not Applicable means the avatar has no groups. Neither is counted as No data."
        data={ingestionMixDev}
        colorFor={connectedEnvIngestionColor}
        total={connectedTotal ?? 0}
        empty="No connected avatars to chart."
      />
    </div>
  );
}

// IR-1705 (Tomer, 2026-10-20 prod video: "when I scroll the pie diagrams
// disappear"). IR-1610 r4 hid the whole strip the moment the list was scrolled,
// so the numbers went with it — the table got its rows back and the operator
// lost every count. The strip now SHRINKS instead: one line, ~34 px, carrying
// the same six tile values, the Connected total and both rings as labelled
// slices. Same numbers by construction (`avatarKpiSummary`); the rings' legend
// words and colours, a coloured dot beside a TEXT label, so colour is never the
// only signal. Zero slices are left out — the rings list every status (r11)
// because a legend row is cheap, but on one line a run of "… 0" entries is what
// would push the bar onto a second line and give the space back.
function KpiBar({
  avatars,
  disconnectedActive,
  onToggleDisconnected,
}: {
  avatars?: FleetAvatar[] | undefined;
  disconnectedActive: boolean;
  onToggleDisconnected: () => void;
}) {
  const { buckets, total, connectedTotal, collectionMix, ingestionMixProd, ingestionMixDev } =
    avatarKpiSummary(avatars);
  const fmt = (n: number | null | undefined) => (n != null ? n.toLocaleString() : "\u2014");
  const value = (n: number | null | undefined) => (
    <span style={{ color: "var(--iris-fg-1)", fontWeight: 600 }}>{fmt(n)}</span>
  );
  // IR-1705 r2 (Harel, 2026-10-20 prod look): the r1 bar put a "·" between
  // every item, and where the line wrapped one was left dangling at the end
  // of a row. No separators now: three groups — the counts, the collection
  // slices, the ingestion slices — each its own wrapping inline-flex, with a
  // wider gap BETWEEN groups than inside one. A wrap then falls between two
  // items or two groups and never strands punctuation.
  const group: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 12px",
  };
  const tiles: { label: string; n: number | null | undefined }[] = [
    { label: "Total", n: total },
    { label: "Ready to Use", n: buckets?.ready },
    { label: "Stale", n: buckets?.stale },
    { label: "Disconnected", n: buckets?.disconnected },
    { label: "Stopped", n: buckets?.stopped },
    { label: "Other", n: buckets?.other },
    { label: "Obsolete", n: buckets?.obsolete },
  ];
  const slices = (
    title: string,
    testId: string,
    data: { label: string; count: number }[],
    colorFor: (label: string) => string | undefined,
  ) => {
    const shown = data.filter((d) => d.count > 0);
    if (shown.length === 0) return null;
    return (
      <span data-testid={testId} style={group}>
        <span style={{ color: "var(--iris-fg-4)" }}>{title}</span>
        {shown.map((d) => (
          <span
            key={d.label}
            data-testid="avatars-stats-bar-slice"
            style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <span
              aria-hidden="true"
              data-testid="avatars-stats-bar-dot"
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                background: colorFor(d.label) ?? "var(--iris-fg-4)",
              }}
            />
            <span>{d.label}</span> {value(d.count)}
          </span>
        ))}
      </span>
    );
  };
  return (
    <div
      data-testid="avatars-stats-bar"
      className="iris-card"
      title="The statistics strip, shrunk to one line. Show stats brings back the tiles and the two rings."
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px 20px",
        padding: "6px 12px",
        minHeight: 34,
        fontSize: 12,
        color: "var(--iris-fg-3)",
      }}
    >
      <span style={group}>
        {tiles.map(({ label, n }) => {
          const testId = `avatars-stats-bar-${label.toLowerCase().replace(/\s+/g, "-")}`;
          return label === "Disconnected" ? (
            // The same one-click filter as the Disconnected tile (IR-1610 r2),
            // so shrinking the strip does not take the shortcut away with it.
            // Same pressed signal too: `aria-pressed` and the inset cyan ring.
            <button
              key={label}
              type="button"
              data-testid={testId}
              aria-pressed={disconnectedActive}
              onClick={onToggleDisconnected}
              title="Click to list only the disconnected avatars; click again to restore the default view."
              style={{
                font: "inherit",
                color: "inherit",
                background: "transparent",
                border: "1px solid var(--iris-line-2)",
                borderRadius: 6,
                padding: "1px 6px",
                cursor: "pointer",
                boxShadow: disconnectedActive ? "inset 0 0 0 1px var(--iris-neon-cyan)" : undefined,
              }}
            >
              {label} {value(n)}
            </button>
          ) : (
            <span key={label} data-testid={testId}>
              {label} {value(n)}
            </span>
          );
        })}
        <span data-testid="avatars-stats-bar-connected">Connected {value(connectedTotal)}</span>
      </span>
      {slices(
        "by collection:",
        "avatars-stats-bar-collection",
        collectionMix,
        connectedCollectionColor,
      )}
      {slices(
        "by ingestion PROD:",
        "avatars-stats-bar-ingestion-prod",
        ingestionMixProd,
        connectedEnvIngestionColor,
      )}
      {slices(
        "by ingestion DEV:",
        "avatars-stats-bar-ingestion-dev",
        ingestionMixDev,
        connectedEnvIngestionColor,
      )}
    </div>
  );
}

// Both r10 rings are the same card with different slices, so the layout, the
// empty state and the "Connected" centre label live in one place — two copies
// would let one ring drift into a different size or a different empty wording.
// IR-1482: also used by the Groups tab. The two new props default to the
// avatar wording, so this file's behaviour is unchanged — Rotem asked for the
// Groups rings to look "like in Avatrs tab", and the comment below already
// warned that a second copy would let one ring drift. A third would be worse.
export function ConnectedDonut({
  testId,
  title,
  hint,
  data,
  colorFor,
  total,
  empty,
  totalLabel = "Connected",
  partialEmpty = "Nothing to chart for the connected avatars.",
  centerValue,
}: {
  testId: string;
  title: string;
  hint: string;
  data: { label: string; count: number }[];
  colorFor: (label: string) => string | undefined;
  total: number;
  empty: string;
  totalLabel?: string;
  partialEmpty?: string;
  /**
   * IR-1482 r2: the centre number, when it is NOT the sum of the slices.
   *
   * On this tab the rings chart exactly the connected avatars, so the sum IS
   * the connected count and this stays unset — behaviour here is unchanged.
   * The Groups tab charts every group while headlining the connected count,
   * so it passes the centre explicitly (Rotem: "Connected by collection,
   * connected by ingestion - NOT ALL").
   */
  centerValue?: number | undefined;
}) {
  return (
    <div
      data-testid={testId}
      className="iris-card"
      style={{
        // Grows into the space the tiles do not take, but never wider than a
        // ring plus its legend needs — two 600px-wide donuts beside a squeezed
        // tile block would be the opposite of what Rotem asked for.
        flex: "1 1 280px",
        maxWidth: 360,
        padding: 12,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
      title={hint}
    >
      <Kicker variant="muted">{title}</Kicker>
      {/* IR-1351 r11: emptiness is now a question about the COUNTS, not about
          the array. Every status is returned on every render (zeros included),
          so `data.length` is a constant (8 or 4 — IR-1720 added the two resume
          rows) and testing it would draw a ring of invisible wedges over a centre reading 0 — precisely the
          "rendering failure" look the empty state exists to avoid. */}
      {data.some((d) => d.count > 0) ? (
        <div style={{ flex: 1, minHeight: 116 }}>
          <DonutWithLegend
            data={data}
            nameKey="label"
            dataKey="count"
            // IR-1769 r5: ten fixed status rows. The chart default of 8 would
            // fold the smallest two into "Other (2)", hiding Stop failed and
            // Resume failed, the rows an operator scans this ring for. These
            // are statuses, not a long tail: never collapse them.
            maxSlices={Math.max(data.length, 8)}
            size={116}
            // The centre reads "Connected" on both rings — it is the number the
            // tile used to carry, and saying it twice is what makes the two
            // rings legible as two views of one population rather than two
            // unrelated charts that happen to sit side by side.
            totalLabel={totalLabel}
            colorFor={colorFor}
            centerValue={centerValue}
          />
        </div>
      ) : (
        // An empty connected population, or the moment before the list lands.
        // Drawing a ring with no slices reads as a rendering failure rather than
        // as "nothing is connected", which is itself the alarming answer.
        <div className="iris-meta" style={{ fontSize: 12 }}>
          {total === 0 ? empty : partialEmpty}
        </div>
      )}
    </div>
  );
}

// ─── Assigned Groups Expander ───

// IR-1450 — the groups the PHONE lists that this environment holds NOTHING for.
//
// Rotem, 2026-09-08: *"the number of reported groups and the number of groups on
// the avatar is always correct — for both environments — even if the groups
// don't send data it should be shown in the UI"*.
//
// The expander above lists ASSIGNMENTS, which exist only once this environment
// has ingested the group. On production that is empty for every V2 avatar,
// because those collectors upload into the dev bucket — so Sophie3_TG1's row
// said "23 on phone" and the expander under it said "No groups assigned", and
// nothing on the screen could name one of the 23.
//
// These rows carry no assignment, so they carry no delete and no Delivering
// mark: the avatar is in the chat and we hold nothing to say anything about.
// Rendering them with the assigned rows' vocabulary would claim a judgement we
// have not made.
function ReportedOnlyGroupList({ rows }: { rows: FleetAvatarReportedGroup[] }) {
  if (rows.length === 0) return null;
  // IR-1743 r2 (Rotem): on production a V2 avatar's whole roster sat under
  // "nothing held here" while the DEV environment held those groups with
  // messages in them — the collector uploads into the dev bucket, so "nothing
  // held HERE" was true and read as "nothing collected". The backend now says,
  // per unheld row, whether DEV holds it (`dev_held`); those rows lead, under
  // their own heading, with DEV's evidence named as DEV. Absent keys (OPS ahead
  // of its backend) leave `devHeld` empty and the list exactly as before.
  const devHeld = rows.filter((r) => r.dev_held === true);
  const rest = rows.filter((r) => r.dev_held !== true);
  return (
    <div style={{ padding: "2px 18px 8px" }} data-testid="reported-only-groups">
      {devHeld.length > 0 && (
        <div data-testid="dev-held-groups">
          <div
            className="iris-meta"
            style={{ fontSize: 11, padding: "6px 0 4px" }}
            title="this environment holds no group for these; the DEV environment does — the date and count below are read from DEV"
          >
            Held on DEV ({devHeld.length})
          </div>
          {devHeld.map((r) => (
            <ReportedOnlyGroupRow key={r.id} r={r} devEvidence />
          ))}
        </div>
      )}
      {rest.length > 0 && (
        <>
          <div
            className="iris-meta"
            style={{ fontSize: 11, padding: "6px 0 4px" }}
            // Says WHERE the gap is, because the same row means two different
            // things: on dev it is usually a group we have not ingested yet; on
            // prod it is the whole roster of a collector that never delivers here.
            title="the handset lists these, and this environment holds no group for them — there is no local assignment or content link to open; collector delivery is shown separately"
          >
            On the phone · nothing held here ({rest.length})
          </div>
          {rest.map((r) => (
            <ReportedOnlyGroupRow key={r.id} r={r} />
          ))}
        </>
      )}
    </div>
  );
}

// IR-1743 r2: DEV's own evidence for a group this environment does not hold.
// Each half is omitted when DEV did not serve it — a missing date is not
// "Invalid Date" and a missing count is not "0" — but a served 0 IS printed:
// DEV holding the group with no messages is a fact Rotem needs to see. "DEV" is
// spelled out on both halves so the number is never mistaken for this
// environment's (colour alone would not say it).
function DevHeldEvidence({ r }: { r: FleetAvatarReportedGroup }) {
  const at = r.dev_last_message_at;
  const hasDate = at != null && at !== "" && !Number.isNaN(Date.parse(at));
  const count = r.dev_message_count;
  const hasCount = typeof count === "number" && Number.isFinite(count);
  return (
    <span
      style={{ display: "inline-flex", gap: 10, textAlign: "right", whiteSpace: "nowrap" }}
      title="read from the DEV environment, which holds this group"
      data-testid="dev-held-evidence"
    >
      {hasDate && (
        <span>
          <span style={{ color: "var(--iris-fg-4)" }}>Last message</span>{" "}
          <span style={{ color: "var(--iris-fg-2)" }}>{fmtLocalDateTimeWithZone(at)}</span>{" "}
          <span style={{ color: "var(--iris-fg-4)" }}>· DEV</span>
        </span>
      )}
      {hasCount && (
        <span>
          <span style={{ color: "var(--iris-fg-2)" }}>{count.toLocaleString()}</span>{" "}
          <span style={{ color: "var(--iris-fg-4)" }}>
            {count === 1 ? "message" : "messages"} on DEV
          </span>
        </span>
      )}
    </span>
  );
}

// One row of the reported-only list, shared by both of its sections (IR-1743
// r2) so a DEV-held row differs from a nothing-held one only by its evidence.
function ReportedOnlyGroupRow({
  r,
  devEvidence = false,
}: { r: FleetAvatarReportedGroup; devEvidence?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "3px 0",
        fontSize: 11,
        position: "sticky",
        left: 0,
        maxWidth: "min(1100px, 96vw)",
      }}
    >
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 380,
          color: "var(--iris-fg-2)",
        }}
        title={r.group_name ?? r.reported_id}
      >
        {r.group_name ?? r.reported_id}
      </span>
      {r.platform && <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{r.platform}</Chip>}
      {/* Same three-state contract the assigned rows keep: unknown stays an
          em-dash, because an avatar that has never reported has told us
          nothing and silence is not a "no". */}
      <span title="was this group in the avatar's most recent roster?">
        {r.on_phone == null ? (
          <span style={{ color: "var(--iris-fg-4)" }}>—</span>
        ) : (
          <StatusPill variant={r.on_phone ? "ok" : "inactive"}>
            {r.on_phone ? "On phone" : "Not in last roster"}
          </StatusPill>
        )}
      </span>
      {(() => {
        const d = groupDeliveryLabel(r);
        const collectedAt = r.delivery_at ?? r.reported_delivered_at;
        return (
          <span
            title={
              collectedAt
                ? `${deliveryPillDetail(d, r.delivery_kind)}. Last collection: ${fmtDate(collectedAt)}`
                : deliveryPillDetail(d, r.delivery_kind)
            }
          >
            {/* IR-1614: the phone can archive a chat it still lists, so the
                Archived pill sits BESIDE "On phone" rather than replacing
                it — both are roster facts about this handset, and the
                second does not cancel the first.
                IR-1707: same colour rule as the assigned rows — a
                member-list-only read keeps its word but not the green. */}
            <StatusPill variant={deliveryPillVariant(d, r.delivery_kind)}>{d.label}</StatusPill>
          </span>
        );
      })()}
      <span style={{ flex: 1, minWidth: 12 }} />
      {devEvidence && <DevHeldEvidence r={r} />}
      {/* IR-1707: the date says what it is — a roster naming the group,
          not a message. */}
      <span
        style={{ minWidth: 92, textAlign: "right", whiteSpace: "nowrap" }}
        title="when a roster last named this group"
        data-testid="reported-in-roster"
      >
        <span style={{ color: "var(--iris-fg-4)" }}>In roster</span>{" "}
        <span style={{ color: "var(--iris-fg-4)" }}>
          {r.last_reported_at ? fmtDate(r.last_reported_at) : "\u2014"}
        </span>
      </span>
    </div>
  );
}

// IR-1425 r10: takes the avatar row too — its `lcp_breakdown` rollup is what
// the strip renders when the backend serves it.
function AvatarGroupList({
  avatarId,
  avatar,
}: { avatarId: number; avatar?: FleetAvatar | undefined }) {
  const { data: groups, isLoading } = useAvatarGroups({ avatar_id: avatarId });
  // IR-1450: served by its own endpoint and its own query — a row here may have
  // no entity, so it can never be merged into the assigned array the delete
  // path keys by id. Falsy-safe: an OPS deployed ahead of its backend (the ops
  // carve-out means that happens routinely) gets undefined and renders the
  // expander exactly as before.
  const { data: reported } = useAvatarReportedGroups(avatarId);
  const reportedOnly = (reported ?? []).filter((r) => !r.held);

  if (isLoading) {
    return (
      <div style={{ padding: "8px 18px", fontSize: 11, color: "var(--iris-fg-4)" }}>Loading...</div>
    );
  }
  if (!groups || groups.length === 0) {
    // "No groups assigned" was the whole answer here, and on a V2 production
    // row it was the misleading half of a true statement: nothing is ASSIGNED
    // and the phone is in 23 chats. The roster now carries the rest.
    // IR-1743 r2: beside a "Held on DEV" section the line read to Rotem as a
    // contradiction — the section IS the answer — so it yields to it there,
    // and stays everywhere else.
    const anyDevHeld = reportedOnly.some((r) => r.dev_held === true);
    return (
      <div>
        {!anyDevHeld && (
          <div style={{ padding: "8px 18px", fontSize: 11, color: "var(--iris-fg-4)" }}>
            No groups assigned
          </div>
        )}
        <ReportedOnlyGroupList rows={reportedOnly} />
      </div>
    );
  }
  // IR-1610 r2 (Rotem, 2026-09-22): "only groups we actually cover; a group
  // not reported by avatar B but by C shows under C only, no 'covered
  // elsewhere' reference in the main list". The main list and its summary
  // are the phone-confirmed (or manual, or never-reported — silence is not
  // evidence) rows; what the phone dropped collapses into the section below.
  // Without the backend fields the split is a no-op and this is today's list.
  const { onPhone, offPhone } = splitAvatarGroups(groups);
  // IR-1610 r5 (Rotem, 2026-10-02): "the group count and the breakdown in the
  // strip must match the list". Summarised from the SAME `onPhone` array the
  // row map below iterates, in the backend's own status words. The
  // delivering / quiet tally that used to sit here is gone from the strip
  // (D-2): the Delivering column and each row's own pill still carry it.
  const summary = summariseGroupStatuses(onPhone);
  // IR-1425 r10 (Rotem, ROUNDS 4): when the backend serves the avatar's own
  // `lcp_breakdown` (held groups by matrix Lifecycle, counted server-side), the
  // strip is THAT — the same words as every row's chip, so strip == rows by
  // construction. `null` (never sent a roster) / absent (pre-port backend) →
  // today's client-side sentence, unchanged.
  const lcpRollup = avatar?.lcp_breakdown ?? null;
  const headSentence = `${summary.total}${summary.total === 1 ? " group on this phone" : " groups on this phone"}`;
  const lcpRollupEntries = lcpRollup
    ? [
        ...(breakdownEntries(lcpRollup)?.map((e) => `${e.count} ${e.label}`) ?? []),
        ...(avatar?.lcp_breakdown_total != null ? [`${avatar.lcp_breakdown_total} total`] : []),
        ...(avatar?.quiet_count != null ? [`Quiet ${avatar.quiet_count}`] : []),
      ]
    : null;
  return (
    // IR-1441 r5: the expander is a `<td colSpan={17}>` INSIDE the table's
    // horizontal scroller, so every row here is as wide as the whole 17-column
    // table — well past the right edge of the window. The delivery pill and its
    // date are pushed there by a `flex: 1` spacer, which put "Covered
    // elsewhere" roughly a thousand pixels off-screen: the summary line was
    // readable and the per-group marks it summarises were not.
    //
    // Same trap the comment above the scroller records from IR-1351 ("silently
    // swallowed the trailing actions cell — invisible AND unclickable"), and it
    // only started to matter here because IR-1441 made these labels
    // load-bearing rather than decoration.
    //
    // `position: sticky; left: 0` pins the content to the scrollport's left
    // edge instead of the table's. Verified against Harel's 2026-09-07 prod
    // screenshots, where the pills were absent from the visible area on E2_1P
    // and S3_1.
    //
    // IR-1705 (Tomer, 2026-10-20 prod video, ~1,970 px window: the DELIVERING /
    // QUIET pills and their dates were "out of the screen" again). r5's width
    // cap was `min(100%, calc(100vw - 220px))` — a guess at the scrollport from
    // the WINDOW's width. The guess only held while the page box was about as
    // wide as the window; the 1440 px page cap made the real scrollport ~1,390
    // px while the cap allowed ~1,750, so the right-aligned marks sat ~360 px
    // past the visible edge. The width is now the scrollport's own measured
    // `clientWidth` (`--avatars-scrollport-w`, set on the scroller), so the
    // marks end at the visible right edge whatever the page or sidebar does.
    // Before the first measurement (and in jsdom, where it reads 0) the
    // fallback is the old `100%`.
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        borderTop: "1px solid var(--iris-line-1)",
        position: "sticky",
        left: 0,
        width: "var(--avatars-scrollport-w, 100%)",
      }}
    >
      <div
        style={{
          background: "var(--iris-bg-3)",
          borderBottom: "1px solid var(--iris-line-1)",
          color: "var(--iris-fg-4)",
          fontSize: 11,
          padding: "8px 36px",
        }}
        data-testid="avatar-group-summary"
        role="note"
        aria-label={
          lcpRollupEntries
            ? `${headSentence} — ${lcpRollupEntries.join(" · ")}`
            : formatGroupStatusSummary(summary)
        }
      >
        <span style={{ color: "var(--iris-fg-3)", fontWeight: 500 }}>{summary.total}</span>
        {summary.total === 1 ? " group on this phone" : " groups on this phone"}
        {lcpRollup && (
          <>
            {" — "}
            <LcpBreakdown
              counts={lcpRollup}
              total={avatar?.lcp_breakdown_total}
              quiet={avatar?.quiet_count}
              populationRule="groups this phone currently holds · no time window"
            />
          </>
        )}
        {!lcpRollup &&
          summary.anyLabelled &&
          summary.buckets.map((b, i) => (
            <Fragment key={b.key}>
              {i === 0 ? " — " : " · "}
              <span style={{ color: "var(--iris-fg-3)", fontWeight: 500 }}>{b.count}</span>
              {` ${b.label}`}
              {b.sub && (
                <>
                  {" ("}
                  {b.sub.map((sub, j) => (
                    <Fragment key={sub.label}>
                      {j > 0 && ", "}
                      <span style={{ color: "var(--iris-fg-3)", fontWeight: 500 }}>
                        {sub.count}
                      </span>
                      {` ${sub.label}`}
                    </Fragment>
                  ))}
                  {")"}
                </>
              )}
            </Fragment>
          ))}
      </div>
      {onPhone.map((ag) => (
        <div
          key={ag.id}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            borderBottom: "1px solid var(--iris-line-1)",
            background: "var(--iris-bg-3)",
            padding: "8px 36px",
            fontSize: 11,
          }}
        >
          {/* IR-1426 r2: the name is BOUNDED, not `flex: 1`. It used to stretch,
              and this row lives inside the tab's horizontally-scrolled table —
              so the name filled the full (wide) table width and shoved every
              mark past the right edge of the viewport. Rotem's 2026-08-24
              screenshot is exactly that: nineteen rows of bare group names, the
              marks present in the DOM and unreachable without scrolling
              sideways. Capping the name and letting an explicit spacer (below)
              absorb the slack is what puts the marks back on screen. */}
          <span
            style={{
              maxWidth: 380,
              flex: "0 1 auto",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              color: "var(--iris-fg-3)",
            }}
            title={ag.group_name ?? ag.group_identifier ?? `#${ag.group_entity_id}`}
          >
            {ag.group_name ?? ag.group_identifier ?? `#${ag.group_entity_id}`}
          </span>
          {ag.platform && <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{ag.platform}</Chip>}
          {/* IR-1610 r2 (Rotem): "group status tags in the expander read from
              the shared calculation" — the backend's `lifecycle_label`, never
              derived here. Absent until the backend ships it. One line, with
              the finer collection label (when present) as the tooltip.
              IR-1610 r5 (D-3): an archived row also SHOWS that collection
              label, so every word in the strip's bracket is on a row. */}
          {/* IR-1425 r10 (Rotem, ROUNDS 4): the matrix Lifecycle is the PRIMARY
              chip — the shared LifecycleChip, same words and colours as the
              Groups tab, reason under it, Quiet as a badge. The grey tag below
              stays verbatim for a row the backend has not served `lcp` on. */}
          {typeof ag.lcp === "string" && (
            <span style={{ display: "inline-flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
              <LifecycleChip
                lcp={ag.lcp}
                label={ag.lcp_label}
                color={ag.lcp_color}
                quiet={ag.lcp_quiet}
                quietWindowDays={7}
                size="sm"
              />
              <LifecycleReason
                lcp={ag.lcp}
                lcp_reason={ag.lcp_reason}
                lcp_reason_label={ag.lcp_reason_label}
                lcp_source={ag.lcp_source}
                lcp_set_at={ag.lcp_set_at}
                lcp_avatar_note={ag.lcp_avatar_note}
              />
            </span>
          )}
          {typeof ag.lcp !== "string" &&
            ag.lifecycle_label &&
            (() => {
              const tag =
                ag.lifecycle === "archived" && ag.collection_label
                  ? `${ag.lifecycle_label} · ${ag.collection_label}`
                  : ag.lifecycle_label;
              return (
                <Chip
                  data-testid="group-lifecycle-tag"
                  title={ag.collection_label ?? ag.lifecycle ?? ag.lifecycle_label}
                  aria-label={`group status: ${tag}`}
                  style={{ fontSize: 10, padding: "2px 8px", whiteSpace: "nowrap" }}
                >
                  {tag}
                </Chip>
              );
            })()}
          {/* IR-1426 r2: the Reported mark sits HERE — immediately beside the
              name — and no longer at the far right. Rotem asked for it "next to
              each group in the expander"; adjacency is the requirement, because
              the fact answers a question about THIS name and a reader scanning
              nineteen rows must pair the two without eye-travel across a wide
              table. Unknown still renders as the em-dash, never "Not reported":
              before an avatar has reported at all, every one of its rows is
              silent, and silence is not evidence. */}
          {(() => {
            const p = groupPhoneMark(ag);
            return (
              <span title={p.detail} data-testid="group-phone-mark">
                {p.tone === "unknown" ? (
                  <span style={{ color: "var(--iris-fg-4)" }}>{p.label}</span>
                ) : (
                  <StatusPill variant={p.tone === "on" ? "ok" : "inactive"}>{p.label}</StatusPill>
                )}
              </span>
            );
          })()}
          {/* IR-1697 r10 (QA finding 1): the same PROD/DEV pair the Groups tab
              prints, from the same cell. Unknown, never a dash, when the row
              carries no word for an environment. */}
          {/* Absent on both = an older backend with no opinion: no cell at all,
              exactly as the avatar row's own pair does. Present-but-null is the
              unreadable shape and reads Unknown. */}
          {(ag.prod_ingestion !== undefined || ag.dev_ingestion !== undefined) && (
            <span data-testid="avatar-group-ingestion">
              <IngestionPairCell
                prodValue={ag.prod_ingestion ?? null}
                prodLabel={ag.prod_ingestion_label ?? null}
                devValue={ag.dev_ingestion ?? null}
                devLabel={ag.dev_ingestion_label ?? null}
                prodReceivedAt={ag.prod_last_ingested_at}
                prodReceiptLabel={ag.prod_receipt_label}
                devReceivedAt={ag.dev_last_ingested_at}
                devReceiptLabel={ag.dev_receipt_label}
                nullAsUnknown
              />
            </span>
          )}
          {/* Absorbs the slack the name used to eat, so the delivery marks stay
              right-aligned without pushing anything off-screen. */}
          <span style={{ flex: 1, minWidth: 12 }} />
          {/* IR-1400: delivering / quiet for THIS avatar. One quiet line among
              healthy ones means that group went silent; every line quiet at
              once means the avatar itself has lost access. */}
          {(() => {
            const d = groupDeliveryLabel(ag);
            const isReported = ag.delivering_scope === "reported";
            // IR-1707: the row's dates are read through these two helpers and
            // nothing else. The collection stamp ("member list OR message")
            // survives only in the pill's tooltip, named for what it is.
            const lastMessage = groupLastMessage(ag);
            const rosterReadAt = groupRosterReadAt(ag);
            const collectedAt = isReported ? (ag.delivery_at ?? ag.reported_delivered_at) : null;
            const detail = deliveryPillDetail(d, ag.delivery_kind);
            // IR-1614: archived on the handset, data still landing. Computed
            // once — the note below renders on it, and the standalone kind
            // span yields to the note so the kind is not printed twice.
            const stillReceiving = archivedStillDelivering(ag);
            return (
              <>
                <span
                  title={`Delivery for THIS avatar in its window — not the group's lifecycle. ${
                    isReported && collectedAt
                      ? `${detail}. Last collection: ${fmtDate(collectedAt)}`
                      : !isReported && ag.last_data_at
                        ? `${detail}. ${fmtDate(ag.last_data_at)}`
                        : detail
                  }`}
                >
                  {/* IR-1614: archived is the one state that DOES get its own
                      colour — the Groups tab already says "archived" in cyan
                      (IR-1487), and this tab must say it in the same voice.
                      Both informational states (stale / unknown) read neutral;
                      the LABEL carries the distinction, so no new colour has to
                      be learned.
                      IR-1707: a member-list-only read keeps its words but not
                      the green — green pills equal the avatar row's headline. */}
                  <StatusPill variant={deliveryPillVariant(d, ag.delivery_kind)}>
                    {/* IR-1610 r2: no "Covered elsewhere" in the main list
                        (Rotem). For THIS avatar the row is quiet; the detail in
                        the title still says who else has it. */}
                    {/* IR-1425 r10: words that cannot be read as a lifecycle —
                        "Delivered today" / "Nothing today" (incl. elsewhere);
                        Unknown / Archived unchanged. Tones untouched. */}
                    {deliveryPillText(d, ag.delivering_window_days ?? null)}
                  </StatusPill>
                </span>
                {/* IR-1614: the phone archived it AND data is still arriving.
                    Both are true, so both are shown — Rotem Ziv's stopped-state
                    contract (IR-1606 c63062): "show the contradiction and actual
                    observation, not a false stopped". The kind word rides along
                    so the reader learns WHAT is still arriving; the date to the
                    right says when. Absent on an archived group that has gone
                    quiet — there the pill is the whole story. */}
                {stillReceiving &&
                  (() => {
                    const kind = deliveryKindLabel(ag.delivery_kind);
                    return (
                      <span
                        style={{ color: "var(--iris-neon-orange)", whiteSpace: "nowrap" }}
                        title="the handset says archived, the collector says data is still landing — both are true; the row is not counted as delivering for this avatar"
                        data-testid="group-archived-still-delivering"
                      >
                        still receiving data{kind === null ? "" : ` · ${kind}`}
                      </span>
                    );
                  })()}
                {/* IR-1425 r8 (Rotem, 2026-09-15): "and detail it here on the
                    expander for sure". The row-level `M: / R:` is only worth
                    trusting if the list under it says which each group was —
                    the IR-1400 r6 rule that the expander and the column must
                    never disagree, applied to the kind as well as the count.

                    Read from `delivery_kind`, which the backend computes from
                    the SAME evidence as the column, rather than re-derived here
                    (a re-derivation is how the two drift apart).

                    Absent for a group that is not delivering: the pill beside
                    it already says so, and a kind for a non-delivery would be
                    inventing one.

                    IR-1614: also absent on an archived row that is still
                    receiving — the "still receiving data" note above already
                    carries the same kind word, and once was enough. */}
                {(() => {
                  const kind = stillReceiving ? null : deliveryKindLabel(ag.delivery_kind);
                  return kind === null ? null : (
                    <span
                      style={{ color: "var(--iris-fg-4)", whiteSpace: "nowrap" }}
                      title={DELIVERY_SPLIT_TITLE}
                      data-testid="group-delivery-kind"
                    >
                      {kind}
                    </span>
                  );
                })()}
                {/* IR-1707: the dates, each NAMED. Line 1 is the group's last
                    MESSAGE — stored, or reported by this avatar's collector —
                    and never a collection stamp. Line 2 is this avatar's last
                    member-list read, rendered only when the backend serves it
                    (absent is unknown, so no dash and no empty line).

                    The old single cell showed `delivery_at` — "member list OR
                    message" — on collector-reported rows, and a collector that
                    re-reads every member list every few hours made every row
                    read as message activity today. The labels are visible
                    because that meaning must not live in a hover. */}
                <span
                  style={{
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 1,
                    whiteSpace: "nowrap",
                    flex: "0 0 auto",
                  }}
                >
                  <span
                    data-testid="group-last-message"
                    title={
                      lastMessage.at === null
                        ? LAST_MESSAGE_NONE_TITLE
                        : `${
                            lastMessage.source === "collector"
                              ? LAST_MESSAGE_COLLECTOR_TITLE
                              : LAST_MESSAGE_STORED_TITLE
                          } — ${new Date(lastMessage.at).toISOString()}`
                    }
                  >
                    <span style={{ color: "var(--iris-fg-4)" }}>Last message</span>{" "}
                    <span style={{ color: "var(--iris-fg-3)" }}>{fmtDateTime(lastMessage.at)}</span>
                  </span>
                  {rosterReadAt !== null && (
                    <span data-testid="group-roster-read" title={ROSTER_READ_TITLE}>
                      <span style={{ color: "var(--iris-fg-4)" }}>Member list</span>{" "}
                      <span style={{ color: "var(--iris-fg-3)" }}>{fmtDateTime(rosterReadAt)}</span>
                    </span>
                  )}
                </span>
              </>
            );
          })()}
          <span
            style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}
            title="assigned to this avatar"
            data-testid="group-assigned-at"
          >
            <span style={{ color: "var(--iris-fg-4)" }}>Assigned</span>{" "}
            <span style={{ color: "var(--iris-fg-3)" }}>{fmtDate(ag.assigned_at)}</span>
          </span>
        </div>
      ))}
      {offPhone.length > 0 && <NotOnPhoneSection rows={offPhone} />}
      <ReportedOnlyGroupList rows={reportedOnly} />
    </div>
  );
}

// IR-1610 r2 (Harel): dropped assignments — the phone reported its groups and
// did not name these — in a COLLAPSED section under the avatar, so the main
// list is exactly what the phone confirms and nothing is silently lost.
// Each row says what we know: "No avatar reports it" when orphaned, "Reported
// by X, Y" when another phone has it, nothing when neither can be claimed.
// Same sticky-left / width-cap container as the main list (IR-1441 r5), so
// the section is on screen at any column count. IR-1705: capped by the
// measured scrollport width, not the window's — see AvatarGroupList.
function NotOnPhoneSection({ rows }: { rows: FleetAvatarGroup[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        position: "sticky",
        left: 0,
        width: "var(--avatars-scrollport-w, 100%)",
        background: "var(--iris-bg-3)",
        borderBottom: "1px solid var(--iris-line-1)",
      }}
    >
      <button
        type="button"
        data-testid="not-on-phone-toggle"
        className="iris-meta"
        aria-expanded={open}
        aria-controls="not-on-phone-rows"
        onClick={() => setOpen((o) => !o)}
        title="assigned here, but the phone's latest roster does not list them"
        style={{
          fontSize: 11,
          padding: "6px 36px",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          cursor: "pointer",
          background: "none",
          border: 0,
          color: "var(--iris-fg-4)",
          font: "inherit",
        }}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        Not on this phone ({rows.length})
      </button>
      {open && (
        <div id="not-on-phone-rows" data-testid="not-on-phone-rows">
          {rows.map((ag) => {
            const note = offPhoneAnnotation(ag);
            const name = ag.group_name ?? ag.group_identifier ?? `#${ag.group_entity_id}`;
            return (
              <div
                key={ag.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  borderTop: "1px solid var(--iris-line-1)",
                  padding: "8px 36px",
                  fontSize: 11,
                }}
              >
                <span
                  style={{
                    maxWidth: 380,
                    flex: "0 1 auto",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--iris-fg-3)",
                  }}
                  title={name}
                >
                  {name}
                </span>
                {ag.platform && (
                  <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{ag.platform}</Chip>
                )}
                <span style={{ flex: 1, minWidth: 12 }} />
                {note?.kind === "orphan" && (
                  <span
                    data-testid="group-orphan-flag"
                    title="no avatar's latest roster lists this group"
                    style={{
                      color: "var(--iris-danger, #e5484d)",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    No avatar reports it
                  </span>
                )}
                {note?.kind === "covered" && (
                  <span
                    data-testid="group-covered-by"
                    title={`listed in the latest roster of: ${note.by.join(", ")}`}
                    style={{ color: "var(--iris-fg-4)", whiteSpace: "nowrap" }}
                  >
                    Reported by {note.by.join(", ")}
                  </span>
                )}
                <span
                  style={{ color: "var(--iris-fg-4)", minWidth: 92, textAlign: "right" }}
                  title="last confirmed on the phone"
                >
                  {fmtDate(ag.last_reported_at ?? null)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Avatar Form Dialog ───

type AvatarFormState = {
  name: string;
  platform: string;
  phone_number: string;
  platform_native_id: string;
  // Telegram-only per-account my.telegram.org app credentials.
  api_id: string;
  api_hash: string;
  // IR-1388: which scraper generation serves this avatar. "" = unset (reads as
  // v1 everywhere). Not offered for Signal, which has no V1/V2 split at all.
  scraper_version: string;
  status: FleetAvatar["status"];
  notes: string;
  device_name: string;
  model: string;
  imei: string;
  // IR-1351: second IMEI (dual-SIM). `imei` above stays IMEI #1.
  imei_2: string;
  // IR-1351: geography the avatar is dedicated to — NOT cover_country below.
  // r4 item 3: the ONE wire string is split in the form — countries picked from
  // the static COUNTRIES list + free text for non-country regions. Serialized
  // back to the single CSV `geo` string on save (parseGeo/serializeGeo).
  geoCountries: string[];
  geoFreeText: string;
  cover_name: string;
  cover_country: string;
  cover_focus: string; // comma-separated in the form; string[] on the wire
  // IR-1307: picked from the backend's canonical vocabulary, so the form holds
  // the array itself — no text to parse, no way to enter an invalid value.
  classifications: string[];
  group_capacity_limit: string; // numeric text input; "" → null (platform default)
};

const EMPTY_FORM: AvatarFormState = {
  name: "",
  platform: "telegram",
  phone_number: "",
  platform_native_id: "",
  api_id: "",
  api_hash: "",
  scraper_version: "",
  // New avatars are 'new' (never linked) so they're eligible for onboarding
  // via the Connect button + the backend's onboard flow.
  status: "new",
  notes: "",
  device_name: "",
  model: "",
  imei: "",
  imei_2: "",
  geoCountries: [],
  geoFreeText: "",
  cover_name: "",
  cover_country: "",
  cover_focus: "",
  classifications: [],
  group_capacity_limit: "",
};

// IR-1226 P1: platform is a fixed choice — the backend whitelists exactly
// these three (avatars_list_create POST); free text created junk rows.
const PLATFORM_OPTIONS = ["telegram", "whatsapp", "signal"];

const STATUS_OPTIONS: DisplayStatus[] = [
  "new",
  // IR-1351 r11: derived, and filter-only — it is deliberately absent from the
  // drawer's Status select. Offering it there would let an operator "set" a
  // value the next render recomputes from the group count and discards.
  "ready_to_use",
  "active",
  "disconnected",
  // IR-1769 (O-17): the Stopped chip.
  "stopped",
  "blocked",
  "cooldown",
  "deleted",
  // IR-1351 r6: obsolete rows ARE in the default view (unlike deleted), so this
  // chip narrows to them rather than revealing them.
  "obsolete",
];

function AvatarFormDialog({
  open,
  onClose,
  editing,
}: {
  open: boolean;
  onClose: () => void;
  editing: FleetAvatar | null;
}) {
  const isEdit = editing !== null;
  const createAvatar = useCreateAvatar();
  const updateAvatar = useUpdateAvatar();
  // IR-1307: canonical vocabulary from the backend. Fall back to whatever this
  // avatar already carries if the request hasn't landed, so an edit dialog can
  // never silently drop existing values by rendering an empty option list.
  const { data: classificationData } = useAvatarClassificationOptions();
  const classificationOptions = classificationFilterOptions(
    classificationData?.classification_options,
    editing ? [editing] : [],
  );

  const [form, setForm] = useState<AvatarFormState>(() => {
    if (!isEdit) return { ...EMPTY_FORM };
    // IR-1351 r4: split the stored geo CSV into picker selections + free text.
    const geoSeed = parseGeo(editing.geo ?? null);
    return {
      name: editing.name,
      platform: editing.platform,
      phone_number: editing.phone_number ?? "",
      platform_native_id: editing.platform_native_id ?? "",
      api_id: editing.api_id ?? "",
      api_hash: editing.api_hash ?? "",
      scraper_version: editing.scraper_version ?? "",
      status: editing.status,
      notes: editing.notes ?? "",
      device_name: editing.device_name ?? "",
      model: editing.model ?? "",
      imei: editing.imei ?? "",
      imei_2: editing.imei_2 ?? "",
      geoCountries: geoSeed.countries,
      geoFreeText: geoSeed.freeText,
      cover_name: editing.cover_name ?? "",
      cover_country: editing.cover_country ?? "",
      cover_focus: coverFocusToInput(editing.cover_focus),
      classifications: editing.classifications ?? [],
      group_capacity_limit:
        editing.group_capacity_limit != null ? String(editing.group_capacity_limit) : "",
    };
  });

  const patch = (p: Partial<AvatarFormState>) => setForm((prev) => ({ ...prev, ...p }));
  const isPending = createAvatar.isPending || updateAvatar.isPending;
  // Telegram avatars carry per-account my.telegram.org api creds; the backend
  // propagates them to the scraper VM (config_sync). Only surface/send for TG.
  const isTelegram = form.platform.trim().toLowerCase() === "telegram";
  // IR-1388: Signal has no V1/V2 split, so it is EXEMPTED from the generation
  // choice rather than defaulted to v1 (Rotem, 2026-08-16). Recomputed off
  // `form.platform` like isTelegram above, so switching Platform mid-edit
  // shows/hides the field immediately.
  const isSignal = form.platform.trim().toLowerCase() === "signal";

  function handleSubmit() {
    const payload: Partial<FleetAvatar> = {
      name: form.name,
      platform: form.platform,
      phone_number: form.phone_number || null,
      platform_native_id: form.platform_native_id || null,
      notes: form.notes || null,
      device_name: form.device_name || null,
      model: form.model || null,
      imei: form.imei || null,
      // IR-1351: "" → null, so clearing the box actually unsets the field
      // rather than storing an empty string the search would still match.
      imei_2: form.imei_2 || null,
      // r4: countries + free text rejoin into the ONE wire string; a fully
      // cleared Geo still PUTs null (serializeGeo returns "" → || null).
      geo: serializeGeo(form.geoCountries, form.geoFreeText) || null,
      cover_name: form.cover_name || null,
      cover_country: form.cover_country || null,
      cover_focus: parseCoverFocus(form.cover_focus),
      classifications: classificationsToPayload(form.classifications),
      group_capacity_limit: parseCapacityLimit(form.group_capacity_limit),
      ...(isTelegram ? { api_id: form.api_id || null, api_hash: form.api_hash || null } : {}),
      // IR-1388: "" → null so clearing the selector actually unsets the column
      // rather than storing an empty string. Never sent for Signal.
      ...(isSignal ? {} : { scraper_version: form.scraper_version || null }),
    };

    if (isEdit) {
      // Send `status` only if the operator changed it (with the seeded
      // baseline so the backend can drop a stale change) — see editStatusFields.
      updateAvatar.mutate(
        {
          id: editing.id,
          ...payload,
          ...editStatusFields(form.status, editing.status, editing.db_status ?? editing.status),
        },
        { onSuccess: onClose },
      );
    } else {
      createAvatar.mutate({ ...payload, status: form.status }, { onSuccess: onClose });
    }
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-[460px] max-w-[92vw]"
        style={{ display: "flex", flexDirection: "column", padding: 0, width: "min(560px, 96vw)" }}
      >
        <div style={{ padding: "24px 24px 12px" }}>
          <SheetTitle>{isEdit ? "Edit Avatar" : "New Avatar"}</SheetTitle>
          <SheetDescription>
            {isEdit ? `Editing "${editing.name}"` : "Add a new fleet avatar"}
          </SheetDescription>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: "0 24px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {/* IR-1351 r5 item 1 (Rotem 2026-08-11): "place the device name &
              model in same line as name (shorten this field)". Name had a
              full-width row to itself and Device Name/Model sat in a 2-col grid
              with the two IMEIs — so the three fields that together identify the
              handset were spread over three lines. One 3-col identity row now
              carries Name | Device Name | Model (which is also what "shorten"
              means for Name: a third of the row, not all of it), and the IMEIs
              get their own 2-col row below. */}
          <div
            data-testid="avatar-identity-row"
            style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Name</span>
              <input
                value={form.name}
                onChange={(e) => patch({ name: e.target.value })}
                placeholder="e.g. Avatar Alpha"
                className="iris-input"
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Device Name</span>
                <FieldInfo text="Device Name becomes the primary display in the table; the internal avatar name stays the scraper-matching key and shows as a tooltip." />
              </span>
              <input
                value={form.device_name}
                onChange={(e) => patch({ device_name: e.target.value })}
                placeholder="e.g. Pixel 7 #12"
                className="iris-input"
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Model</span>
              <input
                value={form.model}
                onChange={(e) => patch({ model: e.target.value })}
                placeholder="e.g. SM-A536B (auto-filled from device farm)"
                className="iris-input"
              />
            </label>
          </div>

          {/* IR-1351: two IMEIs — dual-SIM handsets carry one per slot. #1 is the
              pre-existing `imei`, so nothing that already reads it (including the
              STF device backfill) had to move. r5: they share their own row now
              that Model moved up to the identity row. */}
          <div
            data-testid="avatar-imei-row"
            style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">IMEI #1</span>
              <input
                value={form.imei}
                onChange={(e) => patch({ imei: e.target.value })}
                placeholder="15-digit device IMEI"
                className="iris-input"
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* IR-1351 r4 (Rotem 2026-08-10): the ⓘ was a flex-column SIBLING of
                  the label text, so it rendered on its own line. Same inline-flex
                  wrapper as Device Name puts it beside the text. */}
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">IMEI #2</span>
                <FieldInfo text="Second SIM slot. Entered by hand — the device-farm inventory we auto-fill from reports only one IMEI per device." />
              </span>
              <input
                value={form.imei_2}
                onChange={(e) => patch({ imei_2: e.target.value })}
                placeholder="15-digit device IMEI"
                className="iris-input"
              />
            </label>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Platform</span>
              <select
                value={form.platform}
                onChange={(e) => patch({ platform: e.target.value })}
                className="iris-input"
                style={{ cursor: "pointer" }}
              >
                {PLATFORM_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Status</span>
              <select
                value={form.status}
                onChange={(e) => patch({ status: e.target.value as FleetAvatar["status"] })}
                className="iris-input"
                style={{ cursor: "pointer" }}
              >
                {settableStatusOptions(editing?.status).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* IR-1351 r4 item 2 (Rotem 2026-08-10): Phone Number, Platform Native
              ID and Group Capacity Limit share one 3-column row — three narrow
              identity/limit inputs were each burning a full-width line. */}
          <div
            style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Phone Number</span>
                <FieldInfo text="Required — E.164 (e.g. +46766525016). Recommended: name the avatar by its bare digits (e.g. 46766525016) to match the scraper-VM directory convention." />
              </span>
              <input
                value={form.phone_number}
                onChange={(e) => patch({ phone_number: e.target.value })}
                placeholder="+1234567890"
                className="iris-input"
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Platform Native ID</span>
                <FieldInfo text="The account's own native id — WA JID, TG numeric id, or Signal ACI. Auto-filled from the scraper heartbeat when available; set it here to blacklist this avatar from ingestion (esp. WhatsApp avatars that have no phone)." />
              </span>
              <input
                value={form.platform_native_id}
                onChange={(e) => patch({ platform_native_id: e.target.value })}
                placeholder="447348159060@c.us / 37367780331 / ACI-uuid"
                className="iris-input"
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Group Capacity Limit</span>
                <FieldInfo text="Max groups for this avatar. Leave blank to use the platform default (editable via 'Capacity defaults', next to New Avatar). Display-only for now — enforcement comes later." />
              </span>
              <input
                value={form.group_capacity_limit}
                onChange={(e) => patch({ group_capacity_limit: e.target.value })}
                placeholder="blank → platform default"
                className="iris-input"
                inputMode="numeric"
              />
            </label>
          </div>

          {isTelegram && (
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <span className="iris-field-label">Telegram API ID</span>
                  <FieldInfo text="Per-account app credentials from my.telegram.org. Propagated to the scraper VM so the new agent dir is relink-ready. Required before a Telegram avatar can be authenticated." />
                </span>
                <input
                  value={form.api_id}
                  onChange={(e) => patch({ api_id: e.target.value })}
                  placeholder="e.g. 20257818"
                  className="iris-input"
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="iris-field-label">Telegram API Hash</span>
                <input
                  value={form.api_hash}
                  onChange={(e) => patch({ api_hash: e.target.value })}
                  placeholder="my.telegram.org app hash"
                  className="iris-input"
                />
              </label>
            </div>
          )}

          {!isSignal && (
            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ display: "inline-flex", alignItems: "center" }}>
                  <span className="iris-field-label">Scraper</span>
                  <FieldInfo text="Which scraper generation serves this avatar. Recorded on the avatar now; the Connect flow does NOT act on it yet — onboarding still prepares the V1 scraper on every platform until the scraper-VM side of IR-1388 ships, so setting V2 here does not by itself put the account on the new scraper. Leave unset for the current default. Signal has no V1/V2 split, so this is not offered for it." />
                </span>
                <select
                  value={form.scraper_version}
                  onChange={(e) => patch({ scraper_version: e.target.value })}
                  className="iris-input"
                  data-testid="avatar-scraper-version"
                >
                  <option value="">Unset (default)</option>
                  <option value="v1">V1 (current)</option>
                  <option value="v2">V2 (new)</option>
                </select>
              </label>
            </div>
          )}

          {/* IR-1735 b: read-only per-collector state in the detail, the same
              block as the table cell. Display only; Stop/Start/Relink still
              route by the Scraper value above. */}
          {isEdit && editing?.collectors && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Collectors</span>
              <CollectorsBlock av={editing} testId="avatar-detail-collectors" />
            </div>
          )}

          {/* IR-1351 r4 item 4 (Rotem 2026-08-10): the persona/tasking fields get
              their own labelled section — Cover Name/Country/Focus,
              Classification, Geo and Notes are operational cover data, distinct
              from the device/account identity fields above. Notes moved up from
              the bottom of the form so the section is contiguous. */}
          <div
            data-testid="operational-data-section"
            style={{ display: "flex", flexDirection: "column", gap: 12 }}
          >
            <span className="iris-kicker" style={{ marginTop: 8 }}>
              Operational Data
            </span>

            <div
              style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}
            >
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="iris-field-label">Cover Name</span>
                <input
                  value={form.cover_name}
                  onChange={(e) => patch({ cover_name: e.target.value })}
                  placeholder="persona name"
                  className="iris-input"
                />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span className="iris-field-label">Cover Country</span>
                <input
                  value={form.cover_country}
                  onChange={(e) => patch({ cover_country: e.target.value })}
                  placeholder="e.g. JO"
                  className="iris-input"
                />
              </label>
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Cover Focus</span>
              <input
                value={form.cover_focus}
                onChange={(e) => patch({ cover_focus: e.target.value })}
                placeholder="drugs, cyber, counter-terror (comma-separated)"
                className="iris-input"
              />
            </label>

            {/* IR-1307: multi-select over the backend's canonical IRIS vocabulary.
                The picker only shows a count, so the current selection is echoed as
                chips below it — otherwise the operator has to open the dropdown to
                see what an avatar is already classified as. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Classification</span>
                <FieldInfo text="The IRIS classification(s) this avatar is dedicated to — same vocabulary as an entity's classification. Multi-select; leave empty for unset." />
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                <MultiSelectFilter
                  label="Select…"
                  options={classificationOptions.map((c) => ({ value: c, label: c }))}
                  selected={form.classifications}
                  onChange={(next) => patch({ classifications: next })}
                />
                {form.classifications.length === 0 ? (
                  <span className="iris-meta">None</span>
                ) : (
                  form.classifications.map((c) => (
                    <Chip key={c} style={{ fontSize: 10, padding: "2px 8px" }}>
                      {c}
                    </Chip>
                  ))
                )}
              </div>
            </div>

            {/* IR-1351: Geo sits directly under Classification because Rotem asked
                for it "next to classification" in the list, and the form should not
                contradict the table's grouping. r4 item 3: countries are now a
                picker over the static FE-only COUNTRIES list (r3's "no canonical
                vocabulary" rationale only ever held for REGIONS — Levant, Sahel…
                — which keep the free-text input below). Both serialize into the
                one existing `geo` CSV string, so the API contract and the
                toolbar's raw-substring search are untouched. Picker label must
                not contain "Select" — the Classification tests resolve THEIR
                picker via /Select/. */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                <span className="iris-field-label">Geo</span>
                <FieldInfo text="The geography this avatar is dedicated to. Different from Cover Country above — that is where the persona claims to be from; this is what the avatar is pointed at. Pick countries; use the text box for non-country regions. Searchable from the toolbar." />
              </span>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                <MultiSelectFilter
                  label="Add countries…"
                  options={COUNTRIES.map((c) => ({ value: c, label: c }))}
                  selected={form.geoCountries}
                  onChange={(next) => patch({ geoCountries: next })}
                />
                {form.geoCountries.map((c) => (
                  <Chip key={c} style={{ fontSize: 10, padding: "2px 8px" }}>
                    {c}
                  </Chip>
                ))}
              </div>
              <input
                value={form.geoFreeText}
                onChange={(e) => patch({ geoFreeText: e.target.value })}
                placeholder="non-country regions — e.g. Levant, Sahel"
                className="iris-input"
              />
            </div>

            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="iris-field-label">Notes</span>
              <textarea
                value={form.notes}
                onChange={(e) => patch({ notes: e.target.value })}
                rows={2}
                className="iris-input"
                style={{ resize: "none" }}
              />
            </label>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "12px 24px 24px",
            borderTop: "1px solid var(--iris-line-1)",
          }}
        >
          <button type="button" className="iris-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            disabled={!canSubmitAvatarForm(form) || isPending}
            onClick={handleSubmit}
          >
            {isEdit ? "Save changes" : "Create avatar"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Main ───

const PLATFORM_FILTERS = ["telegram", "whatsapp", "signal"];

// IR-1045 Phase 1: sortable header cell for the avatars custom table (it uses
// a bespoke row layout for the expand toggle + inline edit, so it can't use
// the shared DataTable). Mirrors DataTable's click-to-cycle + arrow icons.
// IR-1351: operator notes rendered in the table. `notes` is the free-text field
// recording WHY an avatar is in the state it is in, and the toolbar search now
// matches it — so this cell has to make the match VISIBLE, not merely present.
// Two rules carried over from the Join Links search (IR-1233):
//   * every backend-matchable string must render a <mark> the analyst can see
//     (r6: a match in an unrendered/hidden column reads as a broken search);
//   * a mark past the truncation boundary is worse than no mark — it is in the
//     DOM but the cell looks unhighlighted — so the value is re-centred on the
//     first occurrence BEFORE it is clamped.
// The full note stays available on hover via `title`.
//
// 28ch, not 48: at 48 the column ate ~400px and pushed the trailing actions
// cell out of the card on a normal window. 28ch matches the Join Links cell
// that solved the same "scannable preview, full value on hover" problem.
const NOTES_VISIBLE_CHARS = 28;
// Exported so the regression test pins the actual class the cell uses rather
// than a copy of it — jsdom does no layout, so a computed-width assertion would
// pass at any width.
export const NOTES_CELL_MAX_WIDTH_CLASS = "max-w-[28ch]";

// IR-1351: the actions column (delete / relink) is PINNED to the right edge.
//
// This table is 1500px of columns in a ~1300px card on a normal window — the
// Notes column is what tipped it over, but the actions cell was already the
// second-widest fixed element and ANY tenth column would have done it. Making
// the card scroll (overflowX:auto, above) stopped the cell being amputated, but
// left it off-screen until you discover a horizontal scrollbar — and the one
// control you must never have to hunt for is Delete.
//
// Sticky keeps it visible while the middle of the table scrolls under it, and
// makes the table immune to this: future columns push the SCROLL, not the
// buttons. Styling lives in iris-kit.css rather than an inline style object
// because an inline background would OUTRANK the `tr:hover td` rule and leave
// the pinned cell as the one cell that never tints on hover.
export const STICKY_ACTIONS_CLASS = "iris-td--sticky-actions";

function NotesCell({ notes, query }: { notes: string | null; query: string }) {
  const text = notes?.trim();
  if (!text) return <td className="iris-td--muted">—</td>;
  const shown = snippetAroundMatch(text, query, {
    visibleWindow: NOTES_VISIBLE_CHARS,
    leadIn: 8,
  });
  return (
    <td className="iris-td--muted">
      <span className={`block truncate ${NOTES_CELL_MAX_WIDTH_CLASS}`} title={text}>
        {highlightMatches(shown, query)}
      </span>
    </td>
  );
}

// IR-1696: inline "Assigned analyst" dropdown on the Avatars tab. Optimistic —
// the select shows the pick immediately rather than waiting for the PUT
// round-trip and the list refetch it triggers — and rolls back to the row's
// last-known value if the save fails, so a rejected write (e.g. a stale
// status_baseline clobber elsewhere in the same payload) never leaves the
// dropdown showing a value the backend never accepted.
function AnalystCell({
  avatar,
  onSave,
}: {
  avatar: FleetAvatar;
  onSave: (id: number, assigned_analyst: string | null) => Promise<unknown>;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const value = pending ?? avatar.assigned_analyst ?? "";

  // The optimistic value clears itself once the row's OWN data confirms it —
  // i.e. once the list refetch `onSave`'s success triggers lands the new
  // value — rather than the instant the PUT promise resolves. Clearing on
  // resolve alone would flash the cell back to the PRE-edit value for the gap
  // between "PUT succeeded" and "the list actually refetched".
  useEffect(() => {
    if (pending !== null && (avatar.assigned_analyst ?? "") === pending) setPending(null);
  }, [avatar.assigned_analyst, pending]);

  async function handleChange(next: string) {
    setFailed(false);
    setPending(next);
    try {
      await onSave(avatar.id, next === "" ? null : next);
    } catch {
      // Roll back to what the row last confirmed, not to "" — a failed save
      // must never look like a successful clear.
      setPending(null);
      setFailed(true);
    }
  }

  return (
    <td>
      <select
        data-testid="avatar-analyst-select"
        aria-label={`Analyst for ${avatar.name}`}
        className="iris-select"
        value={value}
        title={
          failed
            ? "Failed to save — reverted"
            : ASSIGNED_ANALYST_OPTIONS.find((o) => o.value === value)?.full
        }
        style={failed ? { borderColor: "var(--iris-danger, #e5484d)" } : undefined}
        onChange={(e) => void handleChange(e.target.value)}
      >
        <option value="">—</option>
        {ASSIGNED_ANALYST_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </td>
  );
}

// IR-1697 r10: the Ingestion column header sorts by the headline. These two sort
// by PROD or by DEV, chosen explicitly. Same three-click cycle as SortableTh
// (ascending, descending, off); they live beside the filters because the header
// text is the column's name and must stay exactly that.
const INGESTION_ENV_WORDS = { prod: "Production", dev: "Development" } as const;

function IngestionEnvSort({
  sortKey,
  sortDirection,
  onSort,
}: {
  sortKey: string | null;
  sortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
}) {
  return (
    <span
      data-testid="ingestion-env-sort"
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}
    >
      <span style={{ color: "var(--iris-fg-4)" }}>Sort ingestion by</span>
      {(["prod", "dev"] as const).map((env) => {
        const key = `ingestion_${env}`;
        const active = sortKey === key;
        const Icon = !active ? ArrowUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;
        const label = INGESTION_ENV_WORDS[env];
        return (
          <button
            key={env}
            type="button"
            aria-label={`Sort by ${label} ingestion`}
            aria-pressed={active}
            onClick={() => {
              const next: SortDirection = !active ? "asc" : sortDirection === "asc" ? "desc" : null;
              onSort(key, next);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              font: "inherit",
              color: "inherit",
              background: "transparent",
              border: "1px solid var(--iris-line-2)",
              borderRadius: 6,
              padding: "1px 6px",
              cursor: "pointer",
            }}
          >
            {label}
            <Icon
              size={12}
              style={{ color: active ? "var(--iris-neon-cyan)" : "var(--iris-fg-5)" }}
            />
          </button>
        );
      })}
    </span>
  );
}

// IR-1757 r2 (F4): Groups, Delivering and Archived each sort on ONE environment,
// named on the button, as IR-1697 did for Ingestion. Could Not Check sorts as
// null (after every known value), never as 0.
const ENV_VALUE_SORT_COLUMNS = [
  ["groups", "Groups"],
  ["delivering", "Delivering"],
  ["archived", "Archived"],
] as const;

function envSortValue(
  av: FleetAvatar,
  env: "prod" | "dev",
  field: "groups" | "delivering" | "archived",
): number | null {
  const side = av.env_values?.[env];
  if (!side || side.could_not_check) return null;
  if (field === "delivering" && side.delivering_could_not_check) return null;
  const v = side[field];
  return typeof v === "number" ? v : null;
}

function EnvValueSort({
  sortKey,
  sortDirection,
  onSort,
}: {
  sortKey: string | null;
  sortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
}) {
  return (
    <>
      {ENV_VALUE_SORT_COLUMNS.map(([col, title]) => (
        <span
          key={col}
          data-testid={`${col}-env-sort`}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}
        >
          <span style={{ color: "var(--iris-fg-4)" }}>Sort {col} by</span>
          {(["prod", "dev"] as const).map((env) => {
            const key = `${col}_${env}`;
            const active = sortKey === key;
            const Icon = !active ? ArrowUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;
            const label = env === "prod" ? "Production" : "Development";
            return (
              <button
                key={env}
                type="button"
                aria-label={`Sort ${col} by ${label}`}
                title={`${title}, ${label} value`}
                aria-pressed={active}
                onClick={() => {
                  const next: SortDirection = !active
                    ? "asc"
                    : sortDirection === "asc"
                      ? "desc"
                      : null;
                  onSort(key, next);
                }}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  font: "inherit",
                  color: "inherit",
                  background: "transparent",
                  border: "1px solid var(--iris-line-2)",
                  borderRadius: 6,
                  padding: "1px 6px",
                  cursor: "pointer",
                }}
              >
                {label}
                <Icon
                  size={12}
                  style={{ color: active ? "var(--iris-neon-cyan)" : "var(--iris-fg-5)" }}
                />
              </button>
            );
          })}
        </span>
      ))}
    </>
  );
}

function SortableTh({
  colKey,
  label,
  align = "left",
  sortKey,
  sortDirection,
  onSort,
  title,
  width,
  plain = false,
}: {
  colKey: string;
  label: string;
  align?: "left" | "right";
  sortKey: string | null;
  sortDirection: SortDirection;
  onSort: (key: string, direction: SortDirection) => void;
  // IR-1441 r6 (Rotem, 2026-09-07): "i already asked for a tooltip - and you
  // can also change the title. I remind you that I asked these tooltip all
  // across the OPS so it would be clear." Lives on SortableTh rather than on
  // one column so the next count column added can say what it counts.
  title?: string;
  // IR-1697 r17: an explicit column width in px (Avatars Status only).
  width?: number;
  // IR-1757 r2 (F4): no environment-less sort on this header; the column sorts
  // through the labelled Production / Development pair beside the filters.
  plain?: boolean;
}) {
  const handle = () => {
    let next: SortDirection;
    if (sortKey !== colKey) next = "asc";
    else if (sortDirection === "asc") next = "desc";
    else next = null;
    onSort(colKey, next);
  };
  const Icon = sortKey !== colKey ? ArrowUpDown : sortDirection === "asc" ? ArrowUp : ArrowDown;
  const iconColor = sortKey === colKey ? "var(--iris-neon-cyan)" : "var(--iris-fg-5)";
  if (plain) {
    return (
      <th style={{ textAlign: align, whiteSpace: "nowrap", width, minWidth: width }} title={title}>
        {label}
      </th>
    );
  }
  return (
    // IR-1327 r4: `nowrap` on the header cell. Every label here is a short
    // noun phrase sized to the column, and the browser was breaking the
    // two-word ones ("Last Seen", "Not stopped") across two lines, which
    // inflates the height of the whole header row and reads as a layout bug.
    // Applied at SortableTh rather than on one column so the next two-word
    // header added does not reintroduce it.
    <th style={{ textAlign: align, whiteSpace: "nowrap", width, minWidth: width }} title={title}>
      <button
        type="button"
        onClick={handle}
        title={title}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: align === "right" ? "flex-end" : "flex-start",
          width: "100%",
          padding: 0,
          background: "transparent",
          border: "none",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {label}
        <Icon size={12} style={{ color: iconColor, marginLeft: 4 }} />
      </button>
    </th>
  );
}

// ─── IR-1697 screen phase: the per-environment ingestion line ───
//
// "One group that failed out of 100 should flag a fault, but should not make the
// whole avatar look as if it is not ingesting ... with an option to open the
// failed group" (Rotem, 64438). The words come from the backend
// (`<env>_ingestion_label`); this only lays them out. A Partial half is a button
// that opens the failed-groups drill-in (`FailedGroupsDrillIn`, a Sheet like the
// avatar drawer above, AvatarFormDialog). An Unknown half is muted, never red.

/** A group a drill-in row asks the Groups tab to find. By scrapping_id only. */
export interface OpenGroupRequest {
  scrappingId: string;
  env: EnvKey;
}

// IR-1697 receipt round: the age of the last item receipt in one environment,
// the exact UTC instant on hover (the `title` pattern of the Collection cell's
// stoppedDetail line). The WORD is the backend's (`<env>_receipt_label`); this
// composes none, it only places it.
function EnvReceiptStamp({
  env,
  at,
  word,
  wordInText,
  explanation,
}: {
  env: EnvKey;
  at: string;
  word: string;
  wordInText: boolean;
  explanation?: string | undefined;
}) {
  const stamp = (
    <span
      data-testid={`env-receipt-${env}`}
      title={
        explanation ? `${word} ${utcInstant(at)}. ${explanation}` : `${word} ${utcInstant(at)}`
      }
    >
      {wordInText ? null : `${word} `}
      <ReceiptTimeText iso={at} env={env} />
    </span>
  );
  return wordInText ? <> {stamp}</> : <> ({stamp})</>;
}

function EnvIngestionPair({
  parts,
  avatarName,
  onDrill,
  titleFor,
}: {
  parts: EnvIngestionPart[];
  avatarName: string;
  onDrill: (env: EnvKey) => void;
  /** Hover text per value (IR-1327 r7/r8/r9 rulings, applied per environment). */
  titleFor: (p: EnvIngestionPart) => string | undefined;
}) {
  return (
    <div
      className="iris-td--muted"
      style={{ fontSize: 10, whiteSpace: "normal", overflowWrap: "anywhere" }}
      data-testid="ingestion-env-pair"
    >
      {parts.map((p, i) => (
        <Fragment key={p.env}>
          {i > 0 ? " · " : ""}
          {`${p.envLabel}: `}
          {p.kind === "partial" ? (
            <button
              type="button"
              onClick={() => onDrill(p.env)}
              aria-label={`Open failed groups (${p.envLabel}) for ${avatarName}`}
              title="Open the groups that failed"
              style={{
                background: "none",
                border: 0,
                padding: 0,
                font: "inherit",
                cursor: "pointer",
                color: "var(--iris-neon-orange)",
                textDecoration: "underline dotted",
                textUnderlineOffset: 2,
              }}
            >
              {p.text}
            </button>
          ) : p.kind === "unknown" ? (
            <span title="This environment could not be checked, so no count is shown.">
              {p.text}
            </span>
          ) : p.kind === "could_not_check" ? (
            <span title="This deployment cannot read this environment, so nothing is claimed about it. It is not No data.">
              {p.text}
            </span>
          ) : p.kind === "not_applicable" ? (
            <span title={titleFor(p)}>{p.text}</span>
          ) : p.kind === "plain" && p.receivedAt === null ? (
            <span title={titleFor(p)}>{p.text}</span>
          ) : (
            p.text
          )}
          {p.receivedAt && p.receiptLabel ? (
            <EnvReceiptStamp
              env={p.env}
              at={p.receivedAt}
              word={p.receiptLabel}
              // The word is already the text of the line: print only the age.
              wordInText={p.text === p.receiptLabel}
              explanation={titleFor(p)}
            />
          ) : null}
        </Fragment>
      ))}
    </div>
  );
}

function groupStateWords(state: string): string {
  const words = state.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function FailedGroupsDrillIn({
  avatar,
  part,
  onClose,
  onOpenGroup,
}: {
  avatar: FleetAvatar;
  part: EnvIngestionPart;
  onClose: () => void;
  onOpenGroup?: ((g: OpenGroupRequest) => void) | undefined;
}) {
  return (
    <Sheet
      open
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <SheetContent
        side="right"
        style={{ display: "flex", flexDirection: "column", padding: 0, width: "min(560px, 96vw)" }}
      >
        <div style={{ padding: "24px 24px 12px" }}>
          <SheetTitle>Failed groups</SheetTitle>
          <SheetDescription>{`${avatar.name} · ${part.envLabel}`}</SheetDescription>
          <div style={{ marginTop: 8, fontSize: 12, color: "var(--iris-neon-orange)" }}>
            {part.text}
          </div>
        </div>
        <div
          style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px 24px" }}
          data-testid="failed-groups-list"
        >
          {part.failedGroups.map((g) => (
            <div
              key={`${g.scrapping_id}:${g.entity_id ?? ""}`}
              data-testid="failed-group-row"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                borderTop: "1px solid var(--iris-line-1)",
                padding: "8px 0",
                fontSize: 12,
              }}
            >
              <span
                title={g.entity_id != null ? `${part.envLabel} entity #${g.entity_id}` : undefined}
                style={{
                  fontFamily: "var(--iris-font-mono, monospace)",
                  color: "var(--iris-fg-2)",
                }}
              >
                {g.scrapping_id}
              </span>
              <Chip style={{ fontSize: 10, padding: "2px 8px" }}>{groupStateWords(g.state)}</Chip>
              <span style={{ flex: 1, minWidth: 12 }} />
              {onOpenGroup && (
                <button
                  type="button"
                  className="iris-btn"
                  onClick={() => onOpenGroup({ scrappingId: g.scrapping_id, env: part.env })}
                  aria-label={`Open in Groups: ${g.scrapping_id} (${part.envLabel})`}
                  style={{ fontSize: 11, padding: "4px 10px" }}
                >
                  Open in Groups
                </button>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function AvatarsTab({
  onOpenGroup,
}: {
  /** Lets a failed-group row take the operator to that group (Data page wires it). */
  onOpenGroup?: (g: OpenGroupRequest) => void;
} = {}) {
  // IR-1045 Phase 1: platform/status filters move client-side as multi-selects
  // (the fleet/avatars endpoint returns the full list, not server-paginated).
  // Search stays server-side. Status filters on the *effective* (displayed)
  // status so it matches the Status column pill.
  const [platformFilter, setPlatformFilter] = useState<string[]>([]);
  // IR-1610 r2 (Rotem): the seven default statuses are a REAL selection,
  // rendered checked in the chip, so adding Deleted widens the view rather
  // than reading as "Deleted is the only selected state". Never empty: the
  // onChange below and `clearAllFilters` both snap `[]` back to the defaults.
  const [statusFilter, setStatusFilter] = useState<string[]>(() => [...DEFAULT_STATUS_FILTER]);
  // IR-1307: same client-side convention as platform/status above.
  const [classificationFilter, setClassificationFilter] = useState<string[]>([]);
  // IR-1696: same client-side convention — the Analyst column filter.
  const [analystFilter, setAnalystFilter] = useState<string[]>([]);
  // IR-1327 r3 (Rotem): Ingestion and Collection were the only two rendered
  // columns with neither a sort control nor a filter, which is backwards - they
  // are the two an operator scans for trouble. Same client-side convention, but
  // these hold the STATE value rather than the display label.
  // IR-1697 r10: the Ingestion filter once per environment.
  const [ingestionProdFilter, setIngestionProdFilter] = useState<string[]>([]);
  const [ingestionDevFilter, setIngestionDevFilter] = useState<string[]>([]);
  const [collectionFilter, setCollectionFilter] = useState<string[]>([]);
  // IR-1610 r2 (Rotem, 2026-08-25): the Reporting chip — which avatars'
  // phones are reporting their group list, stopped, or never did. Same
  // client-side convention as Ingestion/Collection; empty = no filter.
  const [reportingFilter, setReportingFilter] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  // IR-1351 item 4: "filter failed avatars and view their proxy" (Rotem).
  const [failedOnly, setFailedOnly] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [capacityDefaultsOpen, setCapacityDefaultsOpen] = useState(false);
  const [editing, setEditing] = useState<FleetAvatar | null>(null);
  const [relinkAvatar, setRelinkAvatar] = useState<FleetAvatar | null>(null);
  // IR-1697: which avatar's failed groups (and which environment's) are open.
  const [failedDrill, setFailedDrill] = useState<{ avatarId: number; env: EnvKey } | null>(null);
  // IR-1351 r4 item 6 (rework): the table's own horizontal scrollbar sits at
  // the bottom of a 56-row card — below the viewport fold — so nobody ever saw
  // it. A phantom scrollbar (position: sticky, bottom: 0) mirrors the real
  // scroller's width and stays pinned to the viewport bottom while the table
  // is on screen; scroll positions sync both ways. The width state doubles as
  // the "is there overflow at all" gate — no overflow, no phantom.
  const hScrollRef = useRef<HTMLDivElement | null>(null);
  const hPhantomRef = useRef<HTMLDivElement | null>(null);
  // IR-1479 r3: a second proxy scrollbar ABOVE the header row (see the JSX).
  const hTopRef = useRef<HTMLDivElement | null>(null);
  const [hScrollWidths, setHScrollWidths] = useState({ scroll: 0, client: 0 });
  // Intentionally dep-less — column toggles, data loads and window resizes
  // all change scrollWidth, so re-measure after every render; the
  // prev-equality guard makes the setState a no-op (no render loop) when
  // nothing moved.
  useEffect(() => {
    const measure = () => {
      const el = hScrollRef.current;
      if (!el) return;
      setHScrollWidths((prev) =>
        prev.scroll === el.scrollWidth && prev.client === el.clientWidth
          ? prev
          : { scroll: el.scrollWidth, client: el.clientWidth },
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });
  // IR-1705: `hScrollWidths.client` also feeds `--avatars-scrollport-w`, the
  // width the group expander is capped to (see AvatarGroupList). The effect
  // above re-measures after renders and window resizes; the scrollport can
  // also change width with neither — the page box resizing, a vertical
  // scrollbar appearing as rows load — so observe the element itself too.
  // Guarded like the lock effect: jsdom has no ResizeObserver.
  useEffect(() => {
    const el = hScrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() =>
      setHScrollWidths((prev) =>
        prev.scroll === el.scrollWidth && prev.client === el.clientWidth
          ? prev
          : { scroll: el.scrollWidth, client: el.clientWidth },
      ),
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Unset until measured (0 in jsdom, and on the very first paint), which lets
  // the expander fall back to `100%`. A custom property is not in
  // CSSProperties, hence the cast on this one object only.
  const scrollportWidthVar = (
    hScrollWidths.client > 0 ? { "--avatars-scrollport-w": `${hScrollWidths.client}px` } : {}
  ) as CSSProperties;
  // IR-1610 r2 (Rotem, 2026-09-22: "so find a solution!"). The first sticky
  // header (412dbf0, reverted in bb5c8f5) bounded ONE CARD inside a page that
  // still scrolled, and the measured lock clamped the table to four rows. The
  // premise it lacked: the WHOLE TAB ROOT is viewport-locked, exactly like the
  // Groups tab (IR-1228 r7), so the table wrapper below is the only scrollport
  // and the header has something to stick to. See `04-knowledge/ops-platforms.md`
  // — "A sticky header needs the whole tab locked, not a bounded card".
  //
  // The KPI strip collapses so the list keeps its height (chip + scroll-into-
  // list, remembered under the avatars' own key — one per tab).
  //
  // IR-1705 r2 (Harel, 2026-10-20, looking at r1 on prod in a ~1,370 px
  // window): the first view, before any scroll, showed about one and a half
  // table rows, because the full tiles-and-rings strip is ~300 px tall. Since
  // r1 the one-line `KpiBar` carries every number the strip does (same
  // `avatarKpiSummary`), so opening collapsed costs the operator no count and
  // gives the table its rows back. The default flips for THIS tab only: an
  // explicit stored choice still wins ("0" opens the full strip, "1" the bar),
  // and the Groups tab keeps `loadStatsCollapsed`'s expanded default.
  const [statsCollapsed, setStatsCollapsed] = useState(
    () => storedStatsPreference(localStorage, AVATARS_STATS_STORAGE_KEY) ?? true,
  );
  // IR-1610 r4 (Harel, 2026-10-02, on production). r2 hid the strip when
  // the operator scrolled into the list AND brought it back at the top, and
  // reset the page scroller on every hide — so each crossing of the edge
  // flipped the list between ~320px and ~520px: "the whole screen getting
  // crazy, and rerenders". r3 made the strip manual only, which left a
  // two-row table at the top on a laptop screen: "not comfortable at all".
  // r4 keeps the half that was good — scrolling into the list gives it the
  // page — and removes the half that flickered: the strip NEVER comes back
  // on its own. Once hidden by a scroll it stays hidden until the chip is
  // pressed (or the page is reloaded); no page-scroll reset either. One
  // transition per read of the list, never an oscillation.
  const [statsHiddenByScroll, setStatsHiddenByScroll] = useState(false);
  const statsHidden = statsCollapsed || statsHiddenByScroll;
  const toggleStats = () => {
    // Flip what is ON SCREEN, not the button's own flag (IR-1228 r8): the
    // strip has two reasons to be hidden, and toggling only `statsCollapsed`
    // made the label lie mid-list. An explicit press outranks the scroll.
    const next = !statsHidden;
    setStatsCollapsed(next);
    saveStatsCollapsed(next, localStorage, AVATARS_STATS_STORAGE_KEY);
    setStatsHiddenByScroll(false);
  };
  // Monotonic: `shouldHideStatsForScroll` is consulted only while the strip
  // is still showing; a hidden strip ignores the scroll position entirely.
  const onListScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    setStatsHiddenByScroll((prev) => prev || shouldHideStatsForScroll(el.scrollTop, false));
  };

  // The tab root's height, measured rather than guessed — `lockHeightFor` and
  // its comment explain why the two previous magic numbers both rotted.
  const rootRef = useRef<HTMLDivElement>(null);
  const [lockHeight, setLockHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = () => {
      // Viewport-relative `top` shrinks as the page scrolls; add back the
      // NEAREST real scroller's offset (summing every ancestor double-counts).
      let scrolled = 0;
      for (let n = el.parentElement; n; n = n.parentElement) {
        const oy = getComputedStyle(n).overflowY;
        if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight) {
          scrolled = n.scrollTop;
          break;
        }
      }
      if (scrolled === 0) scrolled = window.scrollY || 0;
      const rect = el.getBoundingClientRect();
      const top = rect.top + scrolled;
      const page = el.closest(".iris-page");
      const pad = page ? Number.parseFloat(getComputedStyle(page).paddingBottom) || 0 : 0;
      // Everything in the tab that is not the table scrollport — derived (root
      // minus wrapper), so a collapsed strip or a wrapped toolbar is accounted
      // for without a constant to maintain. Stable under its own result.
      const card = hScrollRef.current;
      const chrome = card ? Math.max(0, rect.height - card.clientHeight) : 0;
      setLockHeight(lockHeightFor(window.innerHeight, top, pad, chrome));
    };
    measure();
    // Measuring once was the bug on the Groups tab; observe so the lock cannot
    // go stale. Guarded: jsdom (and old browsers) have no ResizeObserver, and
    // an unguarded `new` would throw during commit and take the tab down.
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      const page = el.closest(".iris-page");
      if (page) ro.observe(page);
    }
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Equality check terminates the ping-pong: assigning an equal scrollLeft
  // fires no scroll event, so each side settles after one hop.
  const syncHScroll = (src: HTMLDivElement | null, dst: HTMLDivElement | null) => {
    if (src && dst && dst.scrollLeft !== src.scrollLeft) {
      dst.scrollLeft = src.scrollLeft;
    }
  };
  // IR-1226 P1-B: show/hide columns, persisted per-browser (localStorage).
  const [visibleCols, setVisibleCols] = useState<AvatarColumnKey[]>(() => loadVisibleColumns());
  const show = (k: AvatarColumnKey) => visibleCols.includes(k);
  const onColumnsChange = (v: string[]) => {
    const cols = v as AvatarColumnKey[];
    setVisibleCols(cols);
    saveVisibleColumns(cols);
  };
  // expander + Avatar + actions are always present
  const colCount = 3 + visibleCols.length;

  // IR-1412: `search` is deliberately NOT sent to the server any more. It was
  // the only narrowing control on this tab that re-fetched, which made the
  // fetched list — and therefore the statistics strip counted off it — narrow
  // with the query. It now filters `list` client-side like every chip does, so
  // this fetch is always the whole fleet. See `matchesAvatarSearch`.
  const avatarFilters: Parameters<typeof useFleetAvatars>[0] = {};
  // IR-1327 r2: deleted rows are excluded server-side by default; fetch them
  // only when the user explicitly asks for them via the "Deleted" status chip.
  if (statusFilter.includes("Deleted")) avatarFilters.include_deleted = true;
  const { data: avatars, isLoading, isError, refetch } = useFleetAvatars(avatarFilters);
  // IR-1441 r6: the Delivering header names its own period, read from the
  // response rather than hardcoded — DEV and PROD run different windows while
  // the daily change is being promoted, and a literal "today" would be false on
  // whichever side had not moved yet.
  const deliveringWindow = deliveringWindowDays(avatars);
  const deleteAvatar = useDeleteAvatar();
  const updateAvatar = useUpdateAvatar();
  // IR-1720: the "Resume collection" command (the undo of the IR-1287 stop) and
  // the one sentence shown when a press is refused. A successful press needs no
  // message of its own — the refetch flips the row's Collection cell to "Resume
  // pending", which IS the confirmation. A refused one must not be silent: the
  // operator would otherwise wait on a state that was never going to appear.
  // Rendered in the tab's existing `role="alert"` banner slot (the IR-1412
  // load-failure banner) rather than a native alert or a new toast.
  const resumeCollection = useResumeCollection();
  const [resumeError, setResumeError] = useState<string | null>(null);
  // IR-1696: the inline Analyst dropdown's save path — a plain
  // Partial<FleetAvatar> PUT through the SAME hook the edit dialog uses, so it
  // is subject to the same audit/permission path. mutateAsync so AnalystCell
  // can await it and roll back on rejection.
  const saveAnalyst = (id: number, assigned_analyst: string | null) =>
    updateAvatar.mutateAsync({
      id,
      assigned_analyst: assigned_analyst as "JS" | "ST" | "TS" | "MH" | "RZ" | null,
    });

  const all = avatars ?? [];
  // IR-1705 r3 (Rotem, 2026-10-20: "opens with 2 rows and you have to refresh
  // … it's all a mess"). Reproduced on prod: the fleet call 504'd twice, the
  // retry sat PAUSED (react-query holds retries while the tab is not focused),
  // and a paused query is neither `isLoading` (pending AND fetching) nor
  // `isError` — so `all` fell through to `[]` and the tab announced "0 avatars",
  // six tiles at 0 and "No avatars found". The IR-1412 rule was that a load we
  // do not have must never read as an empty fleet; that rule now keys on the
  // DATA, not on the fetch flags: no list yet and no error ⇒ still loading.
  const notLoadedYet = avatars === undefined && !isError;
  const showLoading = isLoading || notLoadedYet;
  // The strip and the bar get `undefined` (rendered "—") whenever there is no
  // current list to count: failed, or not arrived yet.
  const kpiAvatars = isError ? undefined : avatars;

  const platformOptions = PLATFORM_FILTERS.map((p) => ({ value: p, label: p }));
  const statusOptions = [...new Set([...STATUS_OPTIONS, "stale"].map((s) => statusLabel(s)))].map(
    (label) => ({ value: label, label }),
  );

  const { data: classificationData } = useAvatarClassificationOptions();
  const classificationOptions = classificationFilterOptions(
    classificationData?.classification_options,
    all,
  );

  const list = all.filter(
    (av) =>
      matchesAvatarSearch(av, search) &&
      (platformFilter.length === 0 || platformFilter.includes(av.platform)) &&
      isAvatarVisibleForStatusFilter(effectiveStatus(av), statusFilter, av.db_status) &&
      isAvatarVisibleForClassificationFilter(av.classifications, classificationFilter) &&
      isAvatarVisibleForAnalystFilter(av.assigned_analyst, analystFilter) &&
      (!failedOnly || isAvatarFailing(effectiveStatus(av))) &&
      isAvatarVisibleForEnvIngestionFilter(av, "prod", ingestionProdFilter) &&
      isAvatarVisibleForEnvIngestionFilter(av, "dev", ingestionDevFilter) &&
      isAvatarVisibleForCollectionFilter(collectionState(av), collectionFilter) &&
      isAvatarVisibleForReportingFilter(av, reportingFilter),
  );

  // IR-1327 r4 (Rotem: "we need to add a count for the results"). Counted off
  // the SAME `list` the table renders and the SAME `all` the tiles count, so
  // the line cannot contradict either of them — the r2/r3 failure mode on this
  // screen was three numbers sourced from three different populations.
  const filterCount = avatarFilterCount({
    search,
    platform: platformFilter,
    status: statusFilter,
    classification: classificationFilter,
    analyst: analystFilter,
    ingestion: [],
    ingestionProd: ingestionProdFilter,
    ingestionDev: ingestionDevFilter,
    collection: collectionFilter,
    reporting: reportingFilter,
    failedOnly,
  });

  // IR-1610 r2: the Disconnected KPI tile toggles a Disconnected-only view.
  const disconnectedActive = statusFilter.length === 1 && statusFilter[0] === "Disconnected";
  // IR-1705: one handler for the tile AND the bar's Disconnected item.
  const toggleDisconnected = () =>
    setStatusFilter(disconnectedActive ? [...DEFAULT_STATUS_FILTER] : ["Disconnected"]);

  // One control that empties every narrowing input, including the search box —
  // an operator who cannot see why the list is short needs one way back to the
  // full fleet, not seven.
  function clearAllFilters() {
    setSearch("");
    setPlatformFilter([]);
    setStatusFilter([...DEFAULT_STATUS_FILTER]);
    setClassificationFilter([]);
    setAnalystFilter([]);
    setIngestionProdFilter([]);
    setIngestionDevFilter([]);
    setCollectionFilter([]);
    setReportingFilter([]); // IR-1610 r2
    // IR-1351 r9 (2026-08-13): keep this line, and keep the test beside it.
    //
    // `main` has always cleared Failed-only here. PRODUCTION had a stale comment
    // in its place claiming "no Failed-only toggle on this branch" — which
    // stopped being true when the toggle was hand-patched onto production during
    // the r3 release. Production had the toggle wired everywhere else (the state
    // above, the filter predicate, the button, and `filterCount`), so "Clear all
    // filters" emptied six inputs, left the seventh on, and still reported
    // "1 filter active" — exactly the operator this function exists to rescue:
    // someone who cannot see why the list is short.
    //
    // 🔑 A cherry-pick cannot see a defect shaped like this. It was valid,
    // type-correct code that no incoming commit touched, so it survived every
    // clean pick; it had to be found by DIFFING production against main.
    //
    // The regression test (`avatars-tab.clear-filters.test.tsx`) was written on
    // production and lived only there until 2026-08-31, which left `main` — the
    // branch every future release is cut from — not protecting the guarantee at
    // all. Backported here so the two branches assert the same thing.
    setFailedOnly(false);
  }

  const toMs = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : null);
  const hasEnvValues = list.some((av) => av.env_values != null);
  const { sorted, sortKey, sortDirection, onSort } = useClientSort<FleetAvatar>(
    list,
    {
      name: (av) => av.device_name || av.name,
      platform: (av) => av.platform,
      // IR-1474: a RANK, not the label. Sorting the string put the column in
      // alphabetical order (blocked, connected, cooldown, disconnected, new,
      // obsolete, ready_to_use, stale) - the only attention-bearing column on the
      // tab without a rank function, while Ingestion and Collection beside it had
      // had one since IR-1327 r3.
      status: (av) => statusSortRank(effectiveStatus(av)),
      // IR-1327 r3: severity ranks, not the rendered label - ascending reads
      // healthiest-first so one more click surfaces what needs attention.
      // IR-1697 r10: sort by ONE environment, chosen explicitly. Could Not Check
      // and Not Applicable are null, so they stay after the known values.
      ingestion_prod: (av) => envIngestionSortValue(envIngestionState(av, "prod")),
      ingestion_dev: (av) => envIngestionSortValue(envIngestionState(av, "dev")),
      collection: (av) => collectionSortRank(collectionState(av)),
      // IR-1757 r2 (F4): one environment per key; Could Not Check is null.
      groups_prod: (av) => envSortValue(av, "prod", "groups"),
      groups_dev: (av) => envSortValue(av, "dev", "groups"),
      delivering_prod: (av) => envSortValue(av, "prod", "delivering"),
      delivering_dev: (av) => envSortValue(av, "dev", "delivering"),
      archived_prod: (av) => envSortValue(av, "prod", "archived"),
      archived_dev: (av) => envSortValue(av, "dev", "archived"),
      group_count: (av) => av.group_count,
      // IR-1400 r6: nulls-last is what useClientSort already does, which is right
      // here — a row whose delivery was never computed must not sort as 0.
      groups_delivering: (av) => av.groups_delivering,
      // IR-1426: same nulls-last reasoning as Delivering above — an avatar that
      // has never reported must not sort as though its phone holds 0 groups.
      collected_groups: (av) => av.reported_group_count,
      // IR-1487: same nulls-last reasoning again — an avatar whose emitter never
      // sends an archived count must not sort as though it measured zero.
      archived_groups: (av) => av.reported_archived_count,
      // IR-1479: same nulls-last reasoning a third time. An avatar with no
      // per-cycle count must not sort as though its gap were measured at 0 —
      // "sort by the biggest shortfall" is the whole point of the column, and a
      // V1 row sorting as clean would bury the V2 rows that are actually short.
      not_collected: (av) => av.not_collected,
      last_seen: (av) => toMs(av.last_seen),
      // IR-1515: the date an avatar last actually collected. Sorted on the raw
      // stamp, not the rendered relative string, for the same reason Status is
      // sorted on a rank — "3 days ago" and "3 hours ago" do not compare.
      last_collection: (av) => toMs(av.last_collection_at),
      created_at: (av) => toMs(av.created_at),
      // IR-1696: sorted on the raw initials so the roster groups together.
      analyst: (av) => av.assigned_analyst || "",
    },
    // IR-1474 item 1 - "Please set default sorting: Primary Status". Before this
    // the third argument was simply absent, so `useClientSort` left `sortKey`
    // null and handed the rows straight back: the tab's order was whatever the
    // API sent (`Avatar.Meta.ordering = ['name']`, i.e. name A-Z) and status
    // played no part in it.
    { key: "status", direction: "asc" },
    // IR-1474 item 2 - "Per each source: order by A-Z". There is no Source column
    // on this tab; `platform` (signal / telegram / whatsapp) is the only field an
    // operator would call a source, so a status block is grouped by platform and
    // then alphabetised inside it. Name uses the same `device_name || name`
    // expression the Avatar column renders, so the A-Z the operator reads is the
    // A-Z that was sorted - sorting the raw `name` would have looked wrong on
    // every row where a device name is set.
    // IR-1515 item 2 - "sorted disconnected list - newest on top". FIRST, ahead
    // of the platform grouping IR-1474 added: "newest on top" is a claim about
    // the disconnected list as a whole, and grouping by platform first would
    // bury Friday's Telegram loss under every WhatsApp row. It returns null
    // outside the Disconnected/Stale blocks, so every other block keeps
    // IR-1474's platform-then-name order untouched.
    [
      (av) => disconnectedRecencyRank(effectiveStatus(av), av.last_collection_at),
      (av) => av.platform,
      (av) => av.device_name || av.name,
    ],
  );

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(av: FleetAvatar) {
    setEditing(av);
    setDialogOpen(true);
  }

  function handleDelete(av: FleetAvatar) {
    if (confirm(`Delete avatar "${av.name}"?`)) {
      deleteAvatar.mutate(av.id);
    }
  }

  // The avatars table uses a custom row layout for the expand toggle + inline edit,
  // so we render manually within an iris-card + iris-table structure.
  // IR-1610 r2: the root is viewport-locked (height measured, `minHeight: 0`
  // so the flex children can shrink), the same shape as the Groups tab root.
  // `null` on the very first paint means "not measured yet" — fall back to the
  // calc expression for that one frame rather than render at zero height.
  return (
    <div
      ref={rootRef}
      data-testid="avatars-tab-root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        height: lockHeight === null ? "calc(100vh - 130px)" : lockHeight,
        minHeight: 0,
      }}
    >
      {/* IR-1610 r2: the KPI strip collapses so the list can have its height
          back. Unmounted rather than `display: none` — the two donuts re-render
          on every poll whether or not anyone can see them, and an operator who
          collapsed the strip has said they are not looking at it. The error
          banner stays OUTSIDE it: a failed load must not hide with the numbers. */}
      {/* IR-1412: a failed fetch used to leave `data` undefined, which fell through
          to an empty array — six tiles reading 0, two empty rings and "No avatars
          found". A load that failed and a fleet that is genuinely empty rendered
          the same screen. Passing `undefined` puts the tiles back to "—", and the
          banner below says what actually happened. */}
      {/* IR-1705: hidden no longer means gone — a hidden strip (scrolled past,
          or collapsed by the chip) shrinks to the one-line `KpiBar`, which
          keeps every number on screen at a fraction of the height. The two
          share one container with a stable id so the chip's `aria-controls`
          names an element that exists in both states; the full strip keeps
          its own `avatars-stats-strip` id. */}
      <div id="avatars-stats">
        {/* IR-1757 r1: the status tiles and the Collection ring read the one
            shared fleet row; the two ingestion rings are per environment. */}
        <div className="iris-td--muted" style={{ fontSize: 10, marginBottom: 2 }}>
          <SharedLabel testId="kpi-shared-label" /> · status tiles and Collection ring
        </div>
        {statsHidden ? (
          <KpiBar
            avatars={kpiAvatars}
            disconnectedActive={disconnectedActive}
            onToggleDisconnected={toggleDisconnected}
          />
        ) : (
          <div id="avatars-stats-strip">
            <KpiRow
              avatars={kpiAvatars}
              disconnectedActive={disconnectedActive}
              onToggleDisconnected={toggleDisconnected}
            />
          </div>
        )}
      </div>
      {/* IR-1757 r1 (Rotem 66093): which columns are one value for both
          environments and which are two labelled values. */}
      <div className="iris-td--muted" style={{ fontSize: 11 }} data-testid="avatar-env-legend">
        {SHARED_LABEL}: Status, Collection, Collected groups (the phone&apos;s own count). Shown
        separately for Development and Production: Groups, Delivering, Archived, Ingestion and seats
        held.
      </div>
      {isError && (
        <div
          role="alert"
          className="iris-card"
          style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 12 }}
        >
          <span>Couldn't load the avatar fleet. The numbers above are not current.</span>
          <button type="button" className="iris-btn" onClick={() => refetch?.()}>
            Retry
          </button>
        </div>
      )}
      {/* IR-1720: a refused "Resume collection". Same banner shape as the load
          failure above, and outside the collapsible strip and the scrolling
          table for the same reason — it must be readable wherever the pressed
          row has scrolled to. Says whose resume failed, the server's reason and
          the next step (see resumeErrorMessage); stays until dismissed or the
          next press, so it cannot flash past unread. */}
      {resumeError && (
        <div
          role="alert"
          data-testid="resume-collection-error"
          className="iris-card"
          style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 12 }}
        >
          <span>Could not resume collection for {resumeError}</span>
          <button type="button" className="iris-btn" onClick={() => setResumeError(null)}>
            Dismiss
          </button>
        </div>
      )}

      {/* Toolbar */}
      {/* IR-1705 r2 (Harel, 2026-10-20, prod look at r1 in a ~1,370 px window):
          with every filter AND the three actions in one row, "New Avatar" and
          "Hide stats" fell to a second line — another ~40 px taken from the
          table before the first scroll. The actions moved up to the count line
          below; this row is search + filters + Failed only, which fits one line
          at that width. `flexWrap` stays as the narrow-window fallback. */}
      <div
        data-testid="avatars-toolbar"
        style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search avatars..."
          className="iris-input"
        />
        <MultiSelectFilter
          label="Platform"
          options={platformOptions}
          selected={platformFilter}
          onChange={setPlatformFilter}
        />
        <MultiSelectFilter
          label="Status"
          options={statusOptions}
          selected={statusFilter}
          // IR-1610 r2: unticking the last status, or the menu's own Clear,
          // lands on the defaults — never on an empty list that would match
          // nothing and render an empty table under an unbadged chip.
          onChange={(next) =>
            setStatusFilter(next.length === 0 ? [...DEFAULT_STATUS_FILTER] : next)
          }
        />
        {/* IR-1610 r2 (Rotem, 2026-08-25): "Reporting" — right after Status,
            because the two are read together: an avatar can be Connected and
            still have Stopped Reporting its roster. Buckets come from
            `avatarReportingBucket`, the same 48h rule the Collected groups
            cell uses to tag a stale reading. */}
        <MultiSelectFilter
          label="Reporting"
          options={REPORTING_FILTER_OPTIONS}
          selected={reportingFilter}
          onChange={setReportingFilter}
        />
        <MultiSelectFilter
          label="Classification"
          options={classificationOptions.map((c) => ({ value: c, label: c }))}
          selected={classificationFilter}
          onChange={setClassificationFilter}
        />
        {/* IR-1696: which analyst this avatar is distributed to. */}
        <MultiSelectFilter
          label="Analyst"
          options={ASSIGNED_ANALYST_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          selected={analystFilter}
          onChange={setAnalystFilter}
        />
        {/* IR-1351 item 4 — Rotem: "user can filter failed avatars and view their
            proxy". A single toggle rather than another multi-select: the whole
            interaction is one question ("show me what's broken") asked while
            reading the Proxy column, and a 4th dropdown would bury it. Pressed
            state is announced so it doesn't read as a plain button. */}
        <button
          type="button"
          onClick={() => setFailedOnly((v) => !v)}
          aria-pressed={failedOnly}
          className={failedOnly ? "iris-btn iris-btn--primary" : "iris-btn"}
          title="Show only avatars that are stale, disconnected, blocked or in cooldown — the ones worth checking a proxy for. New avatars are not failures and stay hidden."
        >
          Failed only
        </button>
        {/* IR-1327 r5: the two filters follow the same stage order as the
            columns (Status → Collection → Ingestion). A toolbar that orders
            them one way while the table orders them the other makes the pair
            read as unrelated controls. */}
        <MultiSelectFilter
          label="Collection"
          options={COLLECTION_FILTER_OPTIONS}
          selected={collectionFilter}
          onChange={setCollectionFilter}
        />
        {/* IR-1697 r10/r11: "Make ingestion filters, sorting, statistics and exports
            explicitly environment-specific". One filter per environment; there is
            no environment-less Ingestion filter (Rotem 64438). */}
        <MultiSelectFilter
          label="Ingestion Production"
          options={ENV_INGESTION_FILTER_OPTIONS}
          selected={ingestionProdFilter}
          onChange={setIngestionProdFilter}
        />
        <MultiSelectFilter
          label="Ingestion Development"
          options={ENV_INGESTION_FILTER_OPTIONS}
          selected={ingestionDevFilter}
          onChange={setIngestionDevFilter}
        />
        <IngestionEnvSort sortKey={sortKey} sortDirection={sortDirection} onSort={onSort} />
        {hasEnvValues && (
          <EnvValueSort sortKey={sortKey} sortDirection={sortDirection} onSort={onSort} />
        )}
        <MultiSelectFilter
          label="Columns"
          options={AVATAR_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
          selected={visibleCols}
          onChange={onColumnsChange}
        />
      </div>

      {/* IR-1327 r4: the filtered result count, on its own line at the top of the
          table rather than as muted meta text inside a seven-control toolbar.
          It sits BELOW the KPI tiles on purpose: the tiles are the fleet-wide
          partition (they never move with a filter), this line is what the
          current filter returned, and stacking them makes that difference
          readable instead of leaving a reader to reconcile "Total 56" against a
          9-row table. */}
      {/* IR-1705 r2: this line also carries the tab's three actions, right-
          aligned — the stats chip, Capacity defaults and New Avatar — so the
          filter row above keeps to one line. It already had a button (Clear
          filters) and the room to its right was empty. */}
      <div
        data-testid="avatars-count-row"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
          minHeight: 28,
        }}
      >
        <span style={{ color: "var(--iris-fg-2)", fontSize: 13, fontWeight: 500 }}>
          {showLoading
            ? "Loading..."
            : (() => {
                // IR-1727 follow-up: shown/total/obsoleteShown are decided
                // together by `avatarHeadlineCounts` — see its own comment
                // for the regimes (default, narrowed, retired-only).
                const headline = avatarHeadlineCounts(list, all, statusFilter);
                return avatarResultLabel(
                  headline.shown,
                  headline.total,
                  filterCount,
                  search.trim() !== "",
                  headline.obsoleteShown,
                );
              })()}
        </span>
        {/* IR-1412: gated on the search box too, not only on the chip count.
            Search stopped counting as a filter in this round, and gating the
            button on that count alone would have quietly withdrawn the one-click
            way back to the full fleet from the operator most likely to need it —
            the one staring at an empty table after a query. */}
        {(filterCount > 0 || search.trim() !== "") && (
          <button
            type="button"
            className="iris-btn"
            onClick={clearAllFilters}
            title="Clear every filter and the search box, and show the full fleet again"
          >
            Clear filters
          </button>
        )}
        {/* IR-1610 r2: the stats toggle sits in a row that never leaves the
            screen, so a collapsed strip can always be brought back. That was
            the toolbar until IR-1705 r2 moved it here, to the count line —
            which never scrolls away either (the table below is the only
            scrollport). `aria-expanded` and `aria-controls` keep the state and
            the text agreeing; it is a real button, keyboard-reachable like the
            rest. `marginLeft: auto` pushes the three actions to the right edge. */}
        <button
          type="button"
          className="iris-chip"
          data-testid="avatars-stats-toggle"
          onClick={toggleStats}
          aria-expanded={!statsHidden}
          aria-controls="avatars-stats"
          title={
            statsHidden ? "Show the statistics strip" : "Hide the statistics strip to see more rows"
          }
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            cursor: "pointer",
            marginLeft: "auto",
            // 32px tall like the buttons beside it, so the line keeps one baseline.
            minHeight: 32,
          }}
        >
          {statsHidden ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          {statsHidden ? "Show stats" : "Hide stats"}
        </button>
        <button type="button" className="iris-btn" onClick={() => setCapacityDefaultsOpen(true)}>
          Capacity defaults
        </button>
        <button
          type="button"
          className="iris-btn iris-btn--primary"
          onClick={openCreate}
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={13} /> New Avatar
        </button>
      </div>

      {/* Table */}
      {/* IR-1351: `overflow: hidden` here CLIPPED anything past the card's right
          edge with no way to reach it. With enough columns on (Notes made it
          reproducible on a 1850px window) that silently swallowed the trailing
          actions cell — the delete/relink buttons became invisible AND
          unclickable, not merely off-screen. overflowX:auto keeps the rounded
          corners doing their job while making the tail scrollable instead of
          amputated. Same trap as CD-871 / CD-742. */}
      {/* IR-1351 r4 item 6 (rework): the horizontal scroller is an INNER
          wrapper, not the card — the card must stay overflow-visible or the
          sticky phantom below loses the page as its scrollport and pins to
          nothing. iris-scroll-x styles an always-visible webkit scrollbar on
          both the wrapper and the phantom. */}
      {/* IR-1610 r2: the card claims what the strip and toolbar leave
          (`flex: 1`) and `minHeight: 0` lets it actually shrink to that — a flex
          item defaults to `min-height: auto` and would otherwise grow to fit all
          56 rows and restore the page scroll. It carries NO overflow of its own:
          the wrapper below is the scrollport on both axes. */}
      <div
        className="iris-card"
        style={{ padding: 0, flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {/* IR-1479 r3 (Rotem, 2026-09-07): "Scroll — in the upper side of the
            table". A mirrored horizontal scrollbar above the header row, the
            same proxy as the bottom phantom: an operator reaching for the
            right-hand columns no longer has to page past every row (or find
            the sticky bar at the viewport bottom) to get there. All three —
            top bar, wrapper, phantom — sync scrollLeft to each other; the
            equality check in syncHScroll stops the ping-pong. Hidden outright
            when every column fits, exactly like the phantom. */}
        <div
          ref={hTopRef}
          data-testid="avatars-hscroll-top"
          aria-hidden="true"
          className="iris-scroll-x iris-hscroll-top"
          style={{
            display: hScrollWidths.scroll > hScrollWidths.client ? "block" : "none",
            flexShrink: 0,
          }}
          onScroll={() => {
            syncHScroll(hTopRef.current, hScrollRef.current);
            syncHScroll(hTopRef.current, hPhantomRef.current);
          }}
        >
          <div style={{ width: hScrollWidths.scroll, height: 1 }} />
        </div>
        <div
          ref={hScrollRef}
          data-testid="avatars-hscroll"
          className="iris-scroll-x"
          style={{
            // IR-1610 r2: THE scrollport, on both axes, bounded by the locked
            // tab — which is what `.iris-table--sticky-head` sticks to.
            // `position: relative` is the IR-1228 r7 containing-block fix: the
            // per-row `.sr-only` spans must clip inside this scrollport, or they
            // stretch the app shell by ~1,500px of dead page.
            flex: 1,
            minHeight: 0,
            position: "relative",
            overflowX: "auto",
            overflowY: "auto",
            borderRadius: "inherit",
            // IR-1705: the visible width of this scrollport, for the expander's
            // sticky container (see `scrollportWidthVar`).
            ...scrollportWidthVar,
          }}
          onScroll={(e) => {
            syncHScroll(hScrollRef.current, hPhantomRef.current);
            syncHScroll(hScrollRef.current, hTopRef.current);
            onListScroll(e.currentTarget);
          }}
        >
          <table className="iris-table iris-table--sticky-head">
            <thead>
              <tr>
                <th style={{ width: 24 }} />
                <SortableTh
                  colKey="name"
                  label="Avatar"
                  sortKey={sortKey}
                  sortDirection={sortDirection}
                  onSort={onSort}
                />
                {show("platform") && (
                  <SortableTh
                    colKey="platform"
                    label="Platform"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("status") && (
                  <SortableTh
                    colKey="status"
                    label="Status"
                    width={AVATARS_STATUS_COL_WIDTH}
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {/* IR-1735 b: Collectors, between Status and Collection. Not
                  sortable (a per-collector pair has no single order). */}
                {show("collectors") && (
                  <th
                    data-testid="avatars-collectors-th"
                    title="Which collectors are set up for this avatar and when each last collected. Separate from connection status."
                  >
                    Collectors
                  </th>
                )}
                {/* IR-1327 r5 (Rotem): Status → Collection, "the same as the
                  actual process stages". IR-1479 r3 (Rotem, 2026-09-07) moved
                  Ingestion from directly after Collection to after Archived —
                  see AVATAR_COLUMNS. The header block, the <td> block below and
                  AVATAR_COLUMNS all carry the order independently, so a reorder
                  has to be made in all three; avatars-tab.ir1479-r3-layout.test
                  pins the rendered headers against AVATAR_COLUMNS. */}
                {show("collection") && (
                  <SortableTh
                    colKey="collection"
                    label="Collection"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {/* IR-1697 r14: Ingestion directly after Collection so the per-
                  environment "Last data received ... UTC" text is on the first
                  screen at 1440 (it was after Archived, past the fold; Rotem's
                  IR-1479 r3 order otherwise stands). */}
                {show("ingestion") && (
                  /* IR-1697 r11: not sortable by itself - an environment-less sort
                     would order by a value the column no longer prints. The two
                     "Sort ingestion by PROD / DEV" buttons in the toolbar sort. */
                  <th style={{ whiteSpace: "nowrap" }}>Ingestion</th>
                )}
                {/* IR-1426: the phone's own count, next to ours. Sortable for
                  the same reason Delivering is — "which avatars is the phone
                  holding far more than we assigned?" is a scan down a column.
                  IR-1479 r3 (Rotem, 2026-09-07): directly after Groups, ahead of
                  Delivering — her order. */}
                {show("collected") && (
                  <SortableTh
                    colKey="collected_groups"
                    label="Collected groups"
                    title="How many groups the PHONE itself last reported being in. Comes from the handset, not from OPS's own assignment list, so it can legitimately differ from Groups."
                    align="right"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("delivering") && (
                  <SortableTh
                    colKey="groups_delivering"
                    plain={hasEnvValues}
                    label={deliveringHeaderLabel(deliveringWindow)}
                    title={deliveringHeaderTitle(deliveringWindow)}
                    align="right"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {/* IR-1487: archived chats, beside the collected count and never
                  inside it. Sortable for the same reason as its neighbour —
                  "which handsets are sitting on a pile we never collect?" is a
                  scan down a column. */}
                {show("archived") && (
                  <SortableTh
                    colKey="archived_groups"
                    plain={hasEnvValues}
                    label="Archived"
                    align="right"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {/* IR-1479: the shortfall. Sortable because "which avatars are
                  furthest behind their own phone?" is a scan down a column, and
                  it is the only question on this table that a sort can answer
                  about collection completeness. */}
                {show("not_collected") && (
                  <SortableTh
                    colKey="not_collected"
                    label="Not collected"
                    align="right"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {/* IR-1502: the avatar's media LOAD as a trend. Not sortable yet —
                  Rotem asked to SEE it; a sort key is a separate decision (by
                  level? by rate?) that she has not made. */}
                {show("load") && (
                  <th style={{ textAlign: "right", whiteSpace: "nowrap" }}>{LOAD_COLUMN_LABEL}</th>
                )}
                {show("analyst") && (
                  <SortableTh
                    colKey="analyst"
                    label="Analyst"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("classification") && <th>Classification</th>}
                {show("geo") && <th>Geo</th>}
                {show("proxy") && <th>Proxy</th>}
                {show("scraper") && <th>Scraper</th>}
                {/* IR-1351: Notes precedes the two timestamp columns. THIS block
                  and the <td> block below are the real render order — the
                  AVATAR_COLUMNS array only feeds the Columns dropdown — so a
                  reorder has to be made in three places or the header and the
                  cell drift apart (avatars-tab.ir1479-r3-layout.test pins the
                  three against each other). */}
                {show("notes") && <th>Notes</th>}
                {show("last_seen") && (
                  <SortableTh
                    colKey="last_seen"
                    label="Last Seen"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("last_collection") && (
                  <SortableTh
                    colKey="last_collection"
                    label="Last Collected"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("created") && (
                  <SortableTh
                    colKey="created_at"
                    label="Created"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                {show("cover") && <th>Cover</th>}
                {show("imei") && <th>IMEI #1</th>}
                {show("imei_2") && <th>IMEI #2</th>}
                {show("model") && <th>Model</th>}
                {/* IR-1610 (Rotem, 2026-09-14): "move the old Groups column to
                  be last in table". Last of the data columns, immediately before
                  the sticky actions cell. The header order here is independent of
                  AVATAR_COLUMNS and of the <tbody> below — all three carry it — so
                  moving the column means moving it in three places (IR-1479 r3).

                  IR-1426 r4 (Rotem, 2026-10-02: "Yes!"): the cell now shows what
                  the phone CONFIRMS (`groups_held`); the assigned total is on
                  hover. Sorting deliberately stays on `group_count` this round —
                  the ticket changed what the cell shows, not how the column
                  orders; revisit only if Rotem asks. */}
                {show("groups") && (
                  <SortableTh
                    colKey="group_count"
                    plain={hasEnvValues}
                    label="Groups"
                    title={GROUPS_HEADER_TITLE}
                    align="right"
                    sortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                )}
                <th
                  data-testid="avatar-actions-header"
                  className={STICKY_ACTIONS_CLASS}
                  style={{ width: 48 }}
                />
              </tr>
            </thead>
            <tbody>
              {/* IR-1705 r3: the empty row says WHICH empty it is. Only a list
                  that arrived and matched nothing is "No avatars found"; one
                  still on its way, or one that failed, says so instead. */}
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={colCount}
                    data-testid="avatars-empty-row"
                    style={{ textAlign: "center", color: "var(--iris-fg-4)", padding: "24px 12px" }}
                  >
                    {isError && avatars === undefined
                      ? "Couldn't load the avatar list — press Retry above."
                      : showLoading
                        ? "Loading avatars…"
                        : "No avatars found"}
                  </td>
                </tr>
              )}

              {sorted.map((av) => (
                <Fragment key={av.id}>
                  <tr>
                    {/* Expand toggle */}
                    <td>
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === av.id ? null : av.id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "var(--iris-fg-4)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        {expanded === av.id ? (
                          <ChevronDown size={13} />
                        ) : (
                          <ChevronRight size={13} />
                        )}
                      </button>
                    </td>

                    {/* Name + phone -- clickable to edit */}
                    <td>
                      <button
                        type="button"
                        onClick={() => openEdit(av)}
                        style={{
                          textAlign: "left",
                          fontWeight: 500,
                          color: "var(--iris-fg-1)",
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        <span title={`Avatar name: ${av.name}`}>{av.device_name || av.name}</span>
                        {av.phone_number && (
                          <span style={{ marginLeft: 8, fontSize: 10, color: "var(--iris-fg-4)" }}>
                            {av.phone_number}
                          </span>
                        )}
                      </button>
                    </td>

                    {/* Platform — with the scraper generation riding beside it
                      (IR-1450 r2). Rotem, 2026-09-09: "I critically need V1/V2
                      indication in the OPS so I can properly question you".
                      The Scraper column (IR-1385) already holds this fact but
                      is the 14th of 17 default columns, past the right edge at
                      a normal width, so it read as absent. Same three-way
                      contract as that column, via the same helper: a recorded
                      value is solid, the backend default is a muted question
                      ("V1?"), and Signal — one collector, no split — gets no
                      chip rather than an invented generation. */}
                    {show("platform") && (
                      <td className="iris-td--muted">
                        {(() => {
                          // IR-1479 r3 (Rotem, 2026-09-07): "change platform to
                          // icon so it will be smaller". The same brand glyph the
                          // Groups table renders; PlatformIcon keeps the full name
                          // as an sr-only label + title, so the cell stays
                          // readable by screen readers and by text queries. A
                          // platform with no glyph falls back to the old text.
                          const code = platformToIconCode(av.platform);
                          const gen = scraperGeneration(av.scraper_version, av.platform);
                          const label = gen.known ? gen.label.toUpperCase() : "V1?";
                          const title = gen.known
                            ? `Scraper generation ${label} — recorded on the avatar (scraper_version = ${gen.label})`
                            : "Scraper generation not recorded on this avatar — backend default is v1";
                          return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                              {code ? <PlatformIcon platform={code} /> : av.platform}
                              {gen.applicable && (
                                <span
                                  className={`iris-gen ${gen.known ? "iris-gen--known" : "iris-gen--assumed"}`}
                                  title={title}
                                  aria-label={title}
                                  data-testid="scraper-gen-chip"
                                >
                                  {label}
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                    )}

                    {/* Status pill — PRD label + raw status/reason tooltip */}
                    {show("status") && (
                      <td
                        style={{
                          width: AVATARS_STATUS_COL_WIDTH,
                          minWidth: AVATARS_STATUS_COL_WIDTH,
                        }}
                      >
                        {(() => {
                          const eff = effectiveStatus(av);
                          // IR-1351 r11: `eff` can now be a value the API never
                          // sent, so "raw:" names `av.status` and the derived
                          // word is spelled out beside it — a tooltip that
                          // labelled 'ready_to_use' as raw would send anyone
                          // debugging it looking for a column that has no such
                          // value in it.
                          const title = [
                            av.status_reason,
                            eff === av.status
                              ? `raw: ${av.status}`
                              : `raw: ${av.status} — no groups assigned`,
                          ]
                            .filter(Boolean)
                            .join(" — ");
                          return (
                            <>
                              <span title={title}>
                                <StatusPill variant={statusVariant(eff)}>
                                  {statusLabel(eff)}
                                </StatusPill>
                              </span>
                              {/* IR-1610 r2 (Rotem): "under Disconnected display
                                  the disconnection date/time" — the DATE half of
                                  "clearly identify which avatars disconnected"
                                  (the tile is the other half). Shown for Stale
                                  too: that is a connection that stopped proving
                                  itself. The source (evidence vs recorded) is in
                                  the title. Date-TIME, via formatSampleTime. */}
                              {(eff === "disconnected" || eff === "stale") &&
                                av.disconnected_since && (
                                  <div
                                    className="iris-td--muted"
                                    style={{ fontSize: 10, whiteSpace: "nowrap" }}
                                    data-testid="status-disconnected-since"
                                    title={disconnectedSinceTitle(av.disconnected_since_source)}
                                  >
                                    {disconnectedSincePrefix(av.disconnected_since_source)}{" "}
                                    {formatSampleTime(av.disconnected_since)}
                                  </div>
                                )}
                              {/* IR-1700 O1 (Rotem, comment 64954): a PROVEN auth failure
                                  says WHY and SINCE WHEN. Only under a Disconnected
                                  pill, and only when the backend stored BOTH the
                                  cause and the start — NULL either one and the line
                                  is absent (nothing is invented; the IR-1610 line
                                  above is unaffected). The wording is honest about
                                  the bound: `last_success` is the last good cycle,
                                  `first_seen` is only when it was first noticed. */}
                              {eff === "disconnected" &&
                                av.auth_failure_since &&
                                av.auth_failure_reason && (
                                  <div
                                    className="iris-td--muted"
                                    style={{
                                      fontSize: 10,
                                      display: "flex",
                                      gap: 4,
                                      // IR-1697 r17: width 0 + min 100% takes this line out of the
                                      // column content sizing; the column is the th/td width.
                                      width: 0,
                                      minWidth: "100%",
                                    }}
                                    data-testid="status-reason-line"
                                  >
                                    {/* F4: a long reason is ellipsised (full text in the
                                        title) and the "- since <time>" tail is its own
                                        non-shrinking element, so it is never clipped. */}
                                    <span
                                      data-testid="status-reason-text"
                                      title={av.auth_failure_reason}
                                      style={{
                                        minWidth: 0,
                                        maxWidth: 240,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {av.auth_failure_reason}
                                    </span>{" "}
                                    <span
                                      data-testid="status-reason-since"
                                      style={{ flexShrink: 0, whiteSpace: "nowrap" }}
                                    >
                                      - {authFailureSincePrefix(av.auth_failure_since_source)}{" "}
                                      {formatSampleTime(av.auth_failure_since)}
                                    </span>
                                  </div>
                                )}
                            </>
                          );
                        })()}
                        {av.signal_provision_status && (
                          <StatusPill
                            variant={
                              av.signal_provision_status === "done"
                                ? "ok"
                                : av.signal_provision_status === "failed"
                                  ? "banned"
                                  : "warn"
                            }
                            style={{ marginLeft: 6 }}
                          >
                            {av.signal_provision_status === "done"
                              ? "Provisioned"
                              : av.signal_provision_status === "failed"
                                ? "Provision failed"
                                : "Provisioning…"}
                          </StatusPill>
                        )}
                      </td>
                    )}

                    {/* IR-1735 b (Rotem 65480): the per-collector (V1, V2) cell,
                      its OWN <td> right after Status and before Collection. Read
                      from the saved payload keys; display only. */}
                    {show("collectors") && (
                      <td data-testid="avatar-collectors-cell">
                        <CollectorsBlock av={av} />
                      </td>
                    )}

                    {/* IR-1287: read-only stop-confirmation indicator. A stop the
                      scraper VM never confirmed keeps collecting silently. */}
                    {show("collection") && (
                      <td data-testid="avatar-collection-cell">
                        {(() => {
                          const cs = collectionState(av);
                          // IR-1769 r5 (Rotem 65061): the reason and the last
                          // evidence for Unknown, Not collecting and a
                          // contradicted stop. null for every other state,
                          // which keeps its own tooltip below.
                          const evidence = collectionDetail(av, fmtDateTime);
                          return (
                            <span
                              title={
                                evidence
                                  ? `${evidence.reason} ${evidence.evidence}`
                                  : cs === "stopped"
                                    ? (stoppedDetail(av, fmtDateTime) ??
                                      `Operator stopped collection; scraper VM confirmed ${fmtRelative(
                                        av.collection_stopped_at ?? null,
                                      )}`)
                                    : cs === "stop_pending"
                                      ? // IR-1720: no confirmation is coming
                                        // for Signal, so do not imply one.
                                        isSignalPlatform(av.platform)
                                        ? canIssueResume(av, cs)
                                          ? "This stop cannot be applied for Signal — it never reaches the collection machine. Press Resume collection to clear it."
                                          : "This stop cannot be applied for Signal — it never reaches the collection machine."
                                        : "Stop requested but the scraper VM has not confirmed — it may still be collecting"
                                      : cs === "stop_failed"
                                        ? `Scraper VM could not apply the stop: ${av.collection_stop_error}`
                                        : cs === "not_initiated"
                                          ? "Prepared but never linked, so collection was never started. Set the avatar's status away from New once it is in use."
                                          : // IR-1720: the two resume read-backs.
                                            // Plain words on purpose — operators
                                            // and Rotem read these. The failed
                                            // one carries the machine's own
                                            // reason AND the way out.
                                            cs === "resume_pending"
                                            ? `Resume requested ${fmtRelative(
                                                av.collection_resume_requested_at ?? null,
                                              )}. Reads Running once this avatar has collected again after that time.`
                                            : cs === "resume_failed"
                                              ? `The collection machine could not resume this avatar: ${av.collection_resume_error}. It keeps retrying on its own; to retry by hand, press Stop collection and then Resume collection.`
                                              : cs === "running"
                                                ? "Collecting — data arrived within the last 24 hours"
                                                : "Collecting, but nothing has arrived for over a day"
                              }
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                whiteSpace: "nowrap",
                              }}
                            >
                              <StatusDot variant={collectionVariant(cs)} size={7} />
                              <span
                                style={{
                                  // IR-1327 r6: orange marks every state where
                                  // collection is NOT happening for a reason
                                  // worth a look — the three stop states and
                                  // Not collecting. The running pair and
                                  // 'not_initiated' stay neutral; their dot
                                  // carries the health signal. IR-1769 r5:
                                  // Unknown is neutral too — it claims neither
                                  // health nor failure, and its reason and last
                                  // evidence are on the line beneath. IR-1720: the
                                  // two resume states are orange as well —
                                  // collection is not (yet) happening and
                                  // someone is waiting on it. They land there
                                  // by NOT being in the neutral list below.
                                  color:
                                    cs === "running" ||
                                    cs === "running_no_data" ||
                                    cs === "not_initiated" ||
                                    cs === "unknown"
                                      ? "var(--iris-fg-3)"
                                      : "var(--iris-neon-orange)",
                                }}
                                data-testid="avatar-collection-word"
                              >
                                {collectionLabel(cs)}
                              </span>
                            </span>
                          );
                        })()}
                        {/* IR-1769 r5 (Rotem 65061 C3): "show Unknown with the
                            reason and last known evidence". Visible text, not
                            hover-only; the same sentences are the badge title.
                            Also carries the contradiction note on a Stop pending
                            ("Stop failed: still collected after the stop was
                            required"), a mark on the unchanged state. */}
                        {(() => {
                          const evidence = collectionDetail(av, fmtDateTime);
                          return evidence ? (
                            <div
                              className="iris-td--muted"
                              style={{ fontSize: 10, maxWidth: 260, whiteSpace: "normal" }}
                              data-testid="avatar-collection-unknown-detail"
                            >
                              {evidence.reason} {evidence.evidence}
                            </div>
                          ) : null;
                        })()}
                        {/* IR-1758 r5 (Rotem 65786 option A): the second fact beside
                            the Collection value, visible text in the existing
                            warning tone. The Collection value above is untouched. */}
                        {(() => {
                          const recheck = noGroupsRecheckDetail(av, fmtDateTime);
                          return recheck ? (
                            <div
                              style={{
                                fontSize: 10,
                                maxWidth: 260,
                                whiteSpace: "normal",
                                color: "var(--iris-neon-orange)",
                              }}
                              data-testid="avatar-no-groups-warning"
                            >
                              {recheck.warning}
                              <div
                                className="iris-td--muted"
                                data-testid="avatar-no-groups-next-check"
                              >
                                {recheck.nextCheck}
                              </div>
                            </div>
                          ) : null;
                        })()}
                        {/* IR-1769 (O-47, Rotem 64931): who stopped it, why and
                            when, as visible text (not hover-only). The time is the
                            stop clock; a NULL reads "time not recorded". */}
                        {(() => {
                          const detail = stoppedDetail(av, fmtDateTime);
                          return detail ? (
                            <div
                              className="iris-td--muted"
                              style={{ fontSize: 10, maxWidth: 260, whiteSpace: "normal" }}
                              data-testid="avatar-stopped-detail"
                            >
                              {detail}
                            </div>
                          ) : null;
                        })()}
                        {/* IR-1479 r3 (Rotem, 2026-09-07): "Collection (print
                            last collected at)". The stamp rides under the state
                            as a second muted line. The standalone Last Collected
                            column (IR-1515) stays — this is a second read of the
                            same fact where she asked to see it, not a move. */}
                        {av.last_collection_at && (
                          <div
                            className="iris-td--muted"
                            style={{ fontSize: 10 }}
                            data-testid="collection-last-collected"
                          >
                            last collected {fmtRelative(av.last_collection_at)}
                          </div>
                        )}
                      </td>
                    )}

                    {/* IR-1697 r14: Ingestion sits directly after Collection (moved
                        from after Archived, IR-1479 r3) and BEFORE Collected
                        groups. Kept in step with
                        the <th> block above and AVATAR_COLUMNS. */}
                    {/* IR-1225: read-only ingestion indicator. IR-1697 r11: the
                        single "Ingesting / Last data received / No data" headline
                        is gone; the cell is the PROD and DEV values only. */}
                    {show("ingestion") && (
                      <td>
                        {/* IR-1697: Collection is one shared fact, but Ingestion
                            is per environment — "an avatar can collect and
                            deliver to DEV while no new data reaches PROD".
                            Explicit PROD/DEV line, same order as the Groups
                            tab's prod/dev count pairs. An older backend that omits
                            both fields reads Unknown for each. */}
                        {(() => {
                          const parts = envIngestionPairParts(av);
                          return parts ? (
                            <EnvIngestionPair
                              parts={parts}
                              avatarName={av.name}
                              onDrill={(env) => setFailedDrill({ avatarId: av.id, env })}
                              titleFor={(p) => envPartTitle(p, av, effectiveStatus(av))}
                            />
                          ) : (
                            // An older backend served neither half: the screen cannot
                            // tell, so it says Unknown (never a headline, never blank).
                            <div className="iris-td--muted" style={{ fontSize: 10 }}>
                              PROD: Unknown · DEV: Unknown
                            </div>
                          );
                        })()}
                      </td>
                    )}

                    {/* IR-1426 (Rotem, 2026-08-23): "Collected groups" — the
                        number the HANDSET reported, in its own column so it is
                        never confused with either of ours. The caption is not
                        decoration: this count is routinely LARGER than the
                        assigned one (the WhatsApp emit runs before
                        BLACKLIST_GROUPS; Telegram filters nothing), and without
                        "on phone" under it that gap reads as a counting bug —
                        the exact reflex IR-1400 spent a round removing. There is
                        deliberately NO warning styling for collected > assigned.
                        "—" covers both null (never reported) and a payload that
                        omits the field entirely (prod runs iris-ops ahead of
                        iris-backend), because a 0 would claim the phone holds
                        no groups. */}
                    {show("collected") && (
                      <td className="iris-td--num">
                        {(() => {
                          // IR-1425 r2: date the reading. Until the backend
                          // stopped restamping a replayed roster with arrival
                          // time, every avatar's stamp advanced every 15
                          // minutes and this could never fire — which is how a
                          // WhatsApp roster frozen since 25 June rendered
                          // exactly like one taken a minute ago (IR-1433).
                          const stale = reportStaleness(av.reported_groups_at);
                          // IR-1464 r2 (Rotem, 2026-10-01: "retain the last
                          // known roster [...] and flag the discrepancy [...]
                          // the previous number is last known information —
                          // not a confirmed current count"). When the backend
                          // held the latest report, the number is the LAST
                          // KNOWN roster, not a current reading — same warn
                          // tone as stale, caption says "last known", and the
                          // hover carries her three sentences instead of the
                          // sample-time text.
                          const rejected = rosterRejection(av);
                          // IR-1464 r4 (Rotem 63387 / 65478): where the avatar
                          // is in the verification sequence outranks "last
                          // known": awaiting / failed / verified say more than
                          // a held zero does. `held` and Unknown return null
                          // here, so r2's caption still owns them.
                          const verification = rosterVerification(av);
                          const warn =
                            verification !== null
                              ? verification.tone === "warn"
                              : stale.stale || rejected !== null;
                          return (
                            // IR-1433 r4 (Rotem, 2026-08-26: "I suggest we will
                            // have a tooltip with sample time"): a real Radix
                            // tooltip, not a native `title`. IR-1431 r2 settled
                            // that nine days earlier on this same app — Harel,
                            // looking at prod: "hover on them doesnt display
                            // tooltip". An explanation nobody can surface is not
                            // an explanation, and this is the surface where a
                            // June number and a today number look identical.
                            //
                            // The WHOLE cell is the trigger — number and caption
                            // both — because the caption ("on phone") is what a
                            // reader hovers when they are asking whose number
                            // this is.
                            <StandaloneTooltip>
                              <TooltipTrigger asChild>
                                <div style={{ cursor: "help" }}>
                                  <span
                                    style={
                                      warn ? { color: "var(--iris-warn, #d08a2a)" } : undefined
                                    }
                                  >
                                    {formatCollected(av.reported_group_count)}
                                  </span>
                                  {/* IR-1464 r2: a held report with NO earlier
                                      roster still gets a caption ("unconfirmed")
                                      — the em-dash alone would read as "never
                                      reported", which is the opposite of what
                                      happened. */}
                                  {verification !== null ||
                                  rejected !== null ||
                                  !(
                                    av.reported_group_count === null ||
                                    av.reported_group_count === undefined
                                  ) ? (
                                    <div
                                      className="iris-td--muted"
                                      style={
                                        warn
                                          ? { fontSize: 10, color: "var(--iris-warn, #d08a2a)" }
                                          : { fontSize: 10 }
                                      }
                                    >
                                      {verification !== null
                                        ? verification.caption
                                        : rejected !== null
                                          ? rejected.caption
                                          : stale.stale
                                            ? `on phone · ${stale.date}`
                                            : "on phone"}
                                    </div>
                                  ) : null}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent style={{ maxWidth: 320, whiteSpace: "pre-line" }}>
                                {verification !== null
                                  ? verification.title
                                  : rejected !== null
                                    ? rejected.title
                                    : collectedTitle(
                                        av.reported_group_count,
                                        av.reported_groups_at,
                                        stale,
                                      )}
                              </TooltipContent>
                            </StandaloneTooltip>
                          );
                        })()}
                        {/* IR-1610 (Rotem, 2026-09-14): "instead — show the
                            capacity on the Collected groups column". Moved here
                            verbatim from the Groups cell, which is now the plain
                            assigned count at the far right of the table.

                            🔑 The capacity figure still counts ASSIGNED groups we
                            are still collecting (IR-1226's rule, resolved
                            backend-side in `capacity.py:capacity_usage`) — it is
                            NOT derived from the handset number above it. The two
                            readings share a cell now but answer different
                            questions, which is why the seats line keeps its own
                            tooltip (CAPACITY_TITLE) rather than inheriting the
                            "on phone" one. Rendering them together is her ask;
                            conflating them would be a counting bug. */}
                        {av.capacity_status === "near" && (
                          <StatusPill variant="warn" style={{ marginLeft: 6 }}>
                            Near
                          </StatusPill>
                        )}
                        {av.capacity_status === "at" && (
                          <StatusPill variant="banned" style={{ marginLeft: 6 }}>
                            At capacity
                          </StatusPill>
                        )}
                        {av.capacity_limit ? (
                          <div
                            className="iris-td--muted"
                            style={{ fontSize: 10 }}
                            title={CAPACITY_TITLE}
                            data-testid="collected-capacity-seats"
                          >
                            {av.env_values ? null : (
                              <>
                                {formatCapacity(
                                  collectingSeats(
                                    av.collecting_seats,
                                    av.capacity_used,
                                    av.group_count,
                                  ),
                                  av.capacity_limit,
                                )}{" "}
                                {/* IR-1425 r8 (Rotem, 2026-09-15): "Caption the
                                second line so it reads 14 / 50 still collecting
                                rather than a bare 14 / 50". Without it this
                                number reads as the group total and contradicts
                                the handset figure directly above it — which is
                                the bug Tomer filed on 2026-09-15. */}
                                <span data-testid="collected-capacity-caption">
                                  {CAPACITY_SEATS_CAPTION}
                                </span>
                                {/* IR-1769 (O-43): a Stopped avatar still HOLDS its
                                groups on the phone; they are just not being
                                collected. The held count rides beside the 0. */}
                                {isStoppedAvatar(av) && (
                                  <span
                                    data-testid="collected-capacity-held"
                                    style={{ display: "block" }}
                                  >
                                    {capacitySeats(av.capacity_used, av.group_count)} held, not
                                    collecting
                                  </span>
                                )}
                              </>
                            )}
                            <CapacityEnvCaption env={av.env_values} stopped={isStoppedAvatar(av)} />
                          </div>
                        ) : null}
                        {av.env_values &&
                        av.reported_archived_count !== null &&
                        av.reported_archived_count !== undefined ? (
                          <div
                            className="iris-td--muted"
                            style={{ fontSize: 10 }}
                            data-testid="collected-phone-archived"
                          >
                            {av.reported_archived_count} archived on phone
                          </div>
                        ) : null}
                      </td>
                    )}

                    {/* IR-1400 r6: the delivering count, alone. The number still
                        answers "how much of this coverage is real" — on prod
                        only 452 of 1,828 links delivered inside 7 days — but as
                        its own sortable column instead of a stacked cell. "—"
                        when the response carried no delivering figure (a
                        detail/PUT response runs no page-wide delivery pass; a
                        bare 0 there would claim "nothing is delivering", a
                        different and false statement). */}
                    {show("delivering") && (
                      <td className="iris-td--num">
                        <EnvPairCell env={av.env_values} field="delivering" testId="env-delivering">
                          <span
                            title={
                              // IR-1679 item 4: the rollup that PRODUCES this number is
                              // stale, so the number itself may be frozen from before it
                              // went stale — Ingestion Matrix rule 4, never shown as
                              // current. This check runs BEFORE the null/undefined
                              // check below: a stale response still deserves the "why"
                              // tooltip, not the generic "not computed for this response"
                              // wording that means something different (a detail/PUT
                              // response ran no page-wide pass at all).
                              av.groups_delivering_could_not_check
                                ? deliveringCouldNotCheckTitle(av.groups_delivering_last_checked_at)
                                : av.groups_delivering === null ||
                                    av.groups_delivering === undefined
                                  ? "delivery not computed for this response"
                                  : // IR-1441: the number counts what THIS avatar collected, so the
                                    // wording has to say so — "produced data" read as a fact
                                    // about the groups, which is the bug this ticket removed.
                                    `${deliveringHeaderTitle(av.delivering_window_days ?? null)} Count: ${av.groups_delivering}.`
                            }
                            style={
                              av.groups_delivering_could_not_check
                                ? undefined
                                : isFullyQuiet(av.group_count, av.groups_delivering)
                                  ? { color: "var(--iris-warn, #d08a2a)" }
                                  : undefined
                            }
                          >
                            {av.groups_delivering_could_not_check
                              ? DELIVERING_COULD_NOT_CHECK_LABEL
                              : av.groups_delivering === null || av.groups_delivering === undefined
                                ? "—"
                                : av.groups_delivering}
                          </span>
                          {/* IR-1425 r8 (Rotem, 2026-09-15): the headline above is
                            now MESSAGES ONLY, per IR-1555. This is the split she
                            asked for beside it — never added INTO the headline.
                            r9 (2026-10-01): the shorthand became the metric's own
                            names — Message / Roster only / Observed Total, the
                            IR-1555 matrix's "OPS Counting Rules" — see
                            `formatDeliverySplit`.

                            🔑 `nowrap`: a compact label that wraps to a second
                            line is the one thing that makes this cell unreadable
                            at narrow widths, and the whole complaint on this
                            ticket is two numbers in one cell being misread.
                            The full wording is on `title` for a pointer AND in
                            `aria-label` for everyone else — a hover-only
                            recovery fails the reader who is not hovering. */}
                          {(() => {
                            // IR-1679 item 4: the split partitions the SAME
                            // rollup-produced headline — stale for the same
                            // reason, so it renders nothing rather than a
                            // frozen M:/R:/? breakdown beside "Could Not Check".
                            if (av.groups_delivering_could_not_check) return null;
                            // NOT `groups_delivering` — that is the headline
                            // (message + unknown). `M:` must be the confirmed
                            // subset, or the split would claim a precision the
                            // collectors have not reported yet.
                            const split = formatDeliverySplit(
                              av.groups_delivering_message,
                              av.groups_delivering_roster_only,
                              av.groups_delivering_unknown,
                            );
                            return split === null ? null : (
                              <div
                                className="iris-td--muted"
                                // IR-1425 r9: one metric per line (the string
                                // carries its own breaks) and NO other wrapping —
                                // `pre`, not `pre-line`: on the real column
                                // `pre-line` still broke "Not / reported 217".
                                style={{ fontSize: 10, whiteSpace: "pre", lineHeight: 1.35 }}
                                title={DELIVERY_SPLIT_TITLE}
                                aria-label={DELIVERY_SPLIT_TITLE}
                                data-testid="delivering-kind-split"
                              >
                                {split}
                              </div>
                            );
                          })()}
                        </EnvPairCell>
                      </td>
                    )}

                    {/* IR-1487 (Rotem, 2026-09-04): "the archived should be
                      counted separately and not under collected groups because
                      they are not collected - separate column please".

                      Same null contract as its neighbour, and it is the whole
                      point of the column: "—" for both null (this handset has
                      never reported one) and absent (the payload carries no
                      such key — true of every emitter but the V2 Telegram
                      wrapper, and of any prod where iris-ops runs ahead of
                      iris-backend), and a real **0** when the emitter looked
                      and found none. S10_T1 reports exactly 0, so an em-dash
                      there would discard the only measured reading in the
                      fleet. */}
                    {show("archived") && (
                      <td className="iris-td--num">
                        <EnvPairCell
                          env={av.env_values}
                          field="archived"
                          testId="env-archived"
                          prodFromEnv
                        >
                          <StandaloneTooltip>
                            <TooltipTrigger asChild>
                              <div style={{ cursor: "help" }} data-testid="archived-cell-text">
                                {/* IR-1690 (Rotem, comment 63350): no separate
                                  column — IRIS's own archived count sits
                                  beside the phone's, inside this same cell.
                                  Never summed; see archivedCellText/Title. */}
                                {archivedCellText(
                                  av.iris_archived_count,
                                  av.reported_archived_count,
                                )}
                              </div>
                            </TooltipTrigger>
                            <TooltipContent style={{ maxWidth: 360, whiteSpace: "pre-line" }}>
                              {archivedCellTitle(
                                av.iris_archived_count,
                                av.reported_archived_count,
                              )}
                            </TooltipContent>
                          </StandaloneTooltip>
                        </EnvPairCell>
                      </td>
                    )}

                    {/* IR-1479: the one number on this row that can be non-zero
                      because something is WRONG. Groups is add-only assignment
                      history, Collected groups is the phone's own roster, and
                      Archived is a subset we deliberately skip — none of the
                      three can fall short, so an avatar collecting 4 of its 10
                      groups reads as healthy across all of them.

                      "—" where no per-cycle count exists (the whole V1 fleet),
                      a rendered 0 where we compared the two and found no gap.
                      The warning colour sits on top of a number whose COLUMN
                      already says "Not collected", so the meaning never depends
                      on colour alone. */}
                    {show("not_collected") && (
                      <td className="iris-td--num">
                        <StandaloneTooltip>
                          <TooltipTrigger asChild>
                            <div
                              style={{
                                cursor: "help",
                                ...(coverageGapIsWarning(av.not_collected)
                                  ? { color: "var(--iris-warn, #d08a2a)" }
                                  : {}),
                              }}
                            >
                              {formatCollected(av.not_collected)}
                              {!!av.over_collecting && (
                                <span
                                  style={{ fontSize: 10, color: "var(--iris-warn, #d08a2a)" }}
                                  title={overCollectingTitle(av.over_collecting)}
                                >
                                  {" "}
                                  +{av.over_collecting} over
                                </span>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent style={{ maxWidth: 320, whiteSpace: "pre-line" }}>
                            {av.over_collecting
                              ? overCollectingTitle(av.over_collecting)
                              : coverageGapTitle(
                                  av.not_collected,
                                  av.reported_group_count,
                                  av.collecting_now,
                                )}
                          </TooltipContent>
                        </StandaloneTooltip>
                      </td>
                    )}

                    {/* IR-1502 (Rotem c61810): "Show the TREND, not the level ...
                      color it if it never drains ... full details in tooltip."
                      The headline is the BACKEND's wording, rendered verbatim;
                      "unknown" (never 0) until a collector reports. The colour is
                      never applied to a suspected reset — see loadTone. A real
                      Radix tooltip, not a native title (IR-1433: "hover on them
                      doesnt display tooltip"). */}
                    {show("load") && (
                      <td className="iris-td--num">
                        <StandaloneTooltip>
                          <TooltipTrigger asChild>
                            <div
                              data-load-tone={loadTone(av.load) ?? undefined}
                              style={{
                                cursor: "help",
                                whiteSpace: "nowrap",
                                ...(loadTone(av.load) === "warn"
                                  ? { color: "var(--iris-warn, #d08a2a)" }
                                  : {}),
                              }}
                            >
                              {formatLoad(av.load)}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent style={{ maxWidth: 360, whiteSpace: "pre-line" }}>
                            {loadTitle(av.load)}
                          </TooltipContent>
                        </StandaloneTooltip>
                      </td>
                    )}

                    {/* IR-1696: inline "Assigned analyst" dropdown. */}
                    {show("analyst") && <AnalystCell avatar={av} onSave={saveAnalyst} />}

                    {/* IR-1307: operator-set IRIS classification(s) */}
                    {show("classification") && (
                      <td className="iris-td--muted">
                        {(av.classifications ?? []).length === 0
                          ? "—"
                          : (av.classifications ?? []).map((c) => (
                              <Chip
                                key={c}
                                style={{ fontSize: 10, padding: "2px 8px", marginRight: 4 }}
                              >
                                {c}
                              </Chip>
                            ))}
                      </td>
                    )}

                    {/* IR-1351: Geo — searchable, so the match is marked here for
                      the same reason notes are (IR-1233 r6: whatever the backend
                      matches, the row must SHOW why it matched). */}
                    {show("geo") && (
                      <td className="iris-td--muted">
                        {av.geo?.trim() ? highlightMatches(av.geo.trim(), search) : "—"}
                      </td>
                    )}

                    {/* IR-1351 item 4: VM-reported proxy. Monospace because the
                      whole point is spotting that several rows share one value
                      — proportional digits make that scan much harder. */}
                    {show("proxy") && <td className="iris-td--mono">{av.proxy ?? "—"}</td>}

                    {/* IR-1385: scraper generation. The cell is muted whenever
                      it is NOT a recorded fact — an unset column falling back to
                      "v1 (default)", or a platform with no v1/v2 notion at all.
                      Printing a bare "v1" for the 67 unset rows would make the
                      one Telegram device that genuinely runs v2 look identical
                      to a real v1, which is the precise shape of the IR-1327
                      defect: a rendered value is a claim. `title` carries the
                      distinction for anyone who cannot see the styling. */}
                    {show("scraper") &&
                      (() => {
                        const gen = scraperGeneration(av.scraper_version, av.platform);
                        return (
                          <td className={gen.known ? undefined : "iris-td--muted"}>
                            <span
                              title={
                                gen.known
                                  ? `Recorded on the avatar: scraper_version = ${gen.label}`
                                  : gen.applicable
                                    ? "Not recorded on this avatar — showing the backend default (v1)"
                                    : "Signal has no v1/v2 generation split"
                              }
                            >
                              {gen.label}
                            </span>
                          </td>
                        );
                      })()}

                    {/* Last seen */}
                    {/* IR-1351: operator notes — searchable, with the match
                      marked. Ordered ahead of the timestamps to match the <th>
                      block above; the two are kept in sync by hand. */}
                    {show("notes") && <NotesCell notes={av.notes} query={search} />}

                    {show("last_seen") && (
                      <td className="iris-td--muted">{fmtRelative(av.last_seen ?? null)}</td>
                    )}

                    {/* IR-1515: when data last actually arrived from this
                      avatar. Renders "—" when nothing was ever observed — never
                      a date and never "now", because a blank here means we have
                      no observation, not that the avatar is quiet. */}
                    {show("last_collection") && (
                      <td className="iris-td--muted">
                        {fmtRelative(av.last_collection_at ?? null)}
                        {/* IR-1735 r8 (Rotem 65480): by who. Display only. */}
                        {(() => {
                          const by = lastCollectedBy(av);
                          return by ? (
                            <span data-testid="avatar-last-collected-by">{` · ${by}`}</span>
                          ) : null;
                        })()}
                      </td>
                    )}

                    {/* Created */}
                    {show("created") && (
                      <td className="iris-td--muted">{fmtDate(av.created_at)}</td>
                    )}

                    {/* Cover persona */}
                    {show("cover") && (
                      <td className="iris-td--muted">
                        {av.cover_name ?? "—"}
                        {av.cover_country ? ` · ${av.cover_country}` : ""}
                        {(av.cover_focus ?? []).map((f) => (
                          <Chip key={f} style={{ fontSize: 10, padding: "2px 8px", marginLeft: 6 }}>
                            {f}
                          </Chip>
                        ))}
                      </td>
                    )}
                    {show("imei") && <td className="iris-td--muted">{av.imei ?? "—"}</td>}
                    {show("imei_2") && <td className="iris-td--muted">{av.imei_2 ?? "—"}</td>}
                    {show("model") && <td className="iris-td--muted">{av.model ?? "—"}</td>}

                    {/* IR-1610 (Rotem, 2026-09-14): "move the old Groups column
                        to be last in table". Last data cell before the sticky
                        actions column, matching the <thead> above and
                        AVATAR_COLUMNS — all three carry the order independently
                        (IR-1479 r3), so a move that edits only one of them
                        silently misaligns every row against its headers.

                        The capacity readout is NO LONGER here: IR-1610 moved the
                        Near / At-capacity tag and the "46 / 50" line onto
                        Collected groups.

                        IR-1426 r4 (Rotem, 2026-10-02: "Yes!"): the number is the
                        PHONE-CONFIRMED count (`groups_held`, backend IR-1610
                        PR #901) — E2_1P read 58 while its phone held 1. The
                        assigned total (`group_count`) is on hover, not beside
                        it. Graceful fallback to `group_count` when the field is
                        absent (old backend — ops reaches prod first) or null
                        (a phone that never reported); see groupsColumn(). */}
                    {show("groups") && (
                      <td className="iris-td--num">
                        <EnvPairCell
                          env={av.env_values}
                          field="groups"
                          testId="env-groups"
                          prodFromEnv
                        >
                          <span title={groupsColumn(av).title} data-testid="groups-cell">
                            {groupsColumn(av).shown}
                          </span>
                        </EnvPairCell>
                      </td>
                    )}

                    {/* Delete + Relink — pinned right, see STICKY_ACTIONS_CLASS */}
                    <td data-testid="avatar-actions-cell" className={STICKY_ACTIONS_CLASS}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {/* IR-1225: per-avatar blacklist-coverage control moved to
                          the Blacklist tab (per-row + bulk). The redundant
                          per-row bypass checkbox was removed here. */}
                        {RELINK_ENABLED && effectiveStatus(av) !== "active" && (
                          <button
                            type="button"
                            className="iris-btn iris-btn--primary"
                            style={{ fontSize: 11, padding: "4px 8px" }}
                            onClick={() => setRelinkAvatar(av)}
                          >
                            {relinkActionLabel(av.status)}
                          </button>
                        )}
                        {RELINK_ENABLED && effectiveStatus(av) === "active" && (
                          <button
                            type="button"
                            className="iris-btn"
                            style={{ fontSize: 11, padding: "4px 8px" }}
                            onClick={() => {
                              if (
                                confirm(
                                  `${av.name} appears to be active (delivering data). Force a relink anyway? This wipes the existing session and requires re-pairing on the phone.`,
                                )
                              ) {
                                updateAvatar.mutate(
                                  {
                                    id: av.id,
                                    status: "disconnected",
                                    status_baseline: av.status,
                                    force_relink: true,
                                  },
                                  {
                                    onSuccess: () =>
                                      setRelinkAvatar({ ...av, status: "disconnected" }),
                                  },
                                );
                              }
                            }}
                          >
                            Relink…
                          </button>
                        )}
                        {/* IR-1287 r2: the operator-stop COMMAND. Deliberately a
                          button and not a value in the edit form's status
                          select — `status` is derived, so a quiet avatar already
                          reads `disconnected` there and re-picking it sends
                          nothing (and a native select won't even fire onChange).
                          See canIssueStop. IR-1720: never on a Signal row —
                          the stop cannot be applied there. */}
                        {canIssueStop(av, collectionState(av)) && (
                          <button
                            type="button"
                            className="iris-btn"
                            // IR-1720: `nowrap` is new and changes nothing on
                            // the rows this button already had — it only
                            // matters on a failed stop, the one row where
                            // Resume sits beside it: three buttons in the
                            // pinned cell squeezed both labels onto two lines
                            // and made that row 4px taller than its neighbours
                            // (measured at 1280px). The cell widens instead.
                            style={{ fontSize: 11, padding: "4px 8px", whiteSpace: "nowrap" }}
                            // IR-1720: also held while a Resume is in flight,
                            // so the two commands cannot be fired on top of
                            // each other from one row (both show on Stop failed).
                            disabled={updateAvatar.isPending || resumeCollection.isPending}
                            title="Stop collection: removes this avatar from the scraper VM's scheduler roster"
                            onClick={() => {
                              if (
                                confirm(
                                  `Stop collection for ${av.name}? The scraper VM will drop it from the scheduler roster; an in-flight scrape finishes its current cycle first.`,
                                )
                              ) {
                                updateAvatar.mutate({
                                  id: av.id,
                                  status: "stopped",
                                  status_baseline: av.db_status ?? av.status,
                                });
                              }
                            }}
                          >
                            Stop collection
                          </button>
                        )}
                        {/* IR-1720: the undo of the stop above. Offered only for
                          a PLAIN operator stop — see canIssueResume (the backend
                          answers 409 to anything else, so the button is not
                          shown where it cannot work). On a Signal row it is a
                          record-only undo, and the hover and the question say
                          so instead of promising a catch-up. Same kit button,
                          size and padding as its neighbour, and neither label
                          wraps, so the cluster
                          keeps its height even when both are shown.
                          Disabled while either command is in flight: that is
                          both the in-progress feedback and the double-submit
                          guard. The pressed row reads "Resuming…" meanwhile;
                          success needs no message — the refetch flips the
                          Collection cell to "Resume pending". */}
                        {canIssueResume(av, collectionState(av)) && (
                          <button
                            type="button"
                            className="iris-btn"
                            style={{ fontSize: 11, padding: "4px 8px", whiteSpace: "nowrap" }}
                            disabled={resumeCollection.isPending || updateAvatar.isPending}
                            title={
                              isSignalPlatform(av.platform)
                                ? "Resume collection: clears the stop mark in OPS (for Signal the stop never reached the collection machine)"
                                : "Resume collection: asks the collection machine to start this avatar again"
                            }
                            onClick={() => {
                              if (resumeCollection.isPending) return;
                              if (
                                confirm(
                                  isSignalPlatform(av.platform)
                                    ? `Resume collection for ${av.name}? For Signal the stop never reached the collection machine, so this only clears the stop mark in OPS. The Collection column reads "Resume pending" until the avatar has collected again.`
                                    : `Resume collection for ${av.name}? Collection starts again on the collector's next round and catches up what was missed while it was stopped. The Collection column reads "Resume pending" until the avatar has collected again.`,
                                )
                              ) {
                                setResumeError(null);
                                resumeCollection.mutate(av.id, {
                                  onError: (err) => {
                                    setResumeError(resumeErrorMessage(av.name, err));
                                    // A refusal usually means the row moved under
                                    // us (resumed from another tab, blocked,
                                    // relinked) — reload so it shows where it
                                    // really stands instead of a stale button.
                                    refetch?.();
                                  },
                                });
                              }
                            }}
                          >
                            {resumeCollection.isPending && resumeCollection.variables === av.id
                              ? "Resuming…"
                              : "Resume collection"}
                          </button>
                        )}
                        <button
                          type="button"
                          style={{
                            color: "var(--iris-fg-4)",
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            padding: 4,
                          }}
                          disabled={deleteAvatar.isPending}
                          onClick={() => handleDelete(av)}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded group list */}
                  {expanded === av.id && (
                    <tr>
                      <td colSpan={colCount} style={{ padding: 0 }}>
                        <AvatarGroupList avatarId={av.id} avatar={av} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        {/* Phantom horizontal scrollbar (IR-1351). It existed because the real
            scrollbar sat at the bottom of a 56-row card, below the fold.

            IR-1610 r2: the wrapper above is now a bounded vertical scrollport,
            so its REAL horizontal scrollbar sits at the wrapper's bottom edge —
            on screen, because the tab ends on the fold. Rendering the proxy too
            would stack two identical bars, so it is gated off once the lock has
            been measured. Before the first measurement the wrapper is unbounded
            and the phantom is still the only affordance. The element, its ref
            and its sync handlers stay — and the TOP bar (IR-1479 r3) is
            untouched: in flow above the header, outside the scrollport, still
            the fastest way to reach the right-hand columns. */}
        <div
          ref={hPhantomRef}
          data-testid="avatars-hscroll-phantom"
          className="iris-scroll-x iris-hscroll-phantom"
          style={{
            display:
              lockHeight === null && hScrollWidths.scroll > hScrollWidths.client ? "block" : "none",
          }}
          onScroll={() => {
            syncHScroll(hPhantomRef.current, hScrollRef.current);
            syncHScroll(hPhantomRef.current, hTopRef.current);
          }}
        >
          <div style={{ width: hScrollWidths.scroll, height: 1 }} />
        </div>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <AvatarFormDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setEditing(null);
          }}
          editing={editing}
        />
      )}

      {capacityDefaultsOpen && (
        <CapacityDefaultsDialog
          open={capacityDefaultsOpen}
          onClose={() => setCapacityDefaultsOpen(false)}
        />
      )}

      {/* IR-1697: failed-groups drill-in for a Partial environment. */}
      {failedDrill !== null &&
        (() => {
          const drillAvatar = avatars?.find((a) => a.id === failedDrill.avatarId);
          const part = drillAvatar
            ? envIngestionPairParts(drillAvatar)?.find((p) => p.env === failedDrill.env)
            : undefined;
          return drillAvatar && part && part.kind === "partial" ? (
            <FailedGroupsDrillIn
              avatar={drillAvatar}
              part={part}
              onClose={() => setFailedDrill(null)}
              onOpenGroup={onOpenGroup}
            />
          ) : null;
        })()}

      {/* Relink Wizard */}
      {RELINK_ENABLED && relinkAvatar !== null && (
        <RelinkWizard
          avatarId={relinkAvatar.id}
          avatarName={relinkAvatar.name}
          avatarPlatform={relinkAvatar.platform}
          avatarStatus={relinkAvatar.status}
          expectedGroups={relinkAvatar.group_count}
          onClose={() => setRelinkAvatar(null)}
        />
      )}
    </div>
  );
}
